/// <amd-module name="Wit/WorkItemModel/WorkItem/WorkItem" />
import { translateDisplayNamesToStorageKeys } from "Mention/Common/DisplayNameStorageKeyTranslationService";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { equals, format, localeComparer, newGuid } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { IVssTelemetryService, publishEvent } from "VSS/Platform/Telemetry";
import { mapAsyncSerial } from "VSS/Platform/Util/Array";
import { TreeNodeStructureType, WorkItemErrorPolicy } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking.Types";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import {
    CoreField,
    CoreFieldRefNames,
    DalFields,
    FieldFlags,
    FieldType,
    OobFieldRefNames,
    WITCommonConstants,
    WorkItemLinkConstants
} from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { INode, getNodeSegments, getPath, getReferencedNodeSegmenets } from "Wit/Common/NodeHelpers";
import { IWorkItemNodeService } from "Wit/Common/WorkItemNodeService/IWorkItemNodeService";
import { WorkItemIdentityRef } from "Wit/Identity/Interfaces/WorkItemInterfaces";
import {
    convertPotentialIdentityRefFromFieldValue,
    isWorkItemIdentityRef,
    setResolvedByDescriptorIfWorkItemIdentityRef
} from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import {
    IFieldDataDictionary,
    IFieldLookup,
    IFieldProjectData,
    ILinkInfo,
    IProjectDataWithNodes,
    IReferencedNode,
    IWorkItemData,
    IWorkItemTypeData,
    LinkCommand
} from "Wit/WorkItemDataManager/Contracts";
import { IWorkItemDataManager } from "Wit/WorkItemDataManager/WorkItemDataManager";
import {
    BugWITFieldReferenceNames,
    CoreLinkTypes,
    Exceptions,
    LinkTypeName,
    WITCustomerIntelligenceArea,
    WITCustomerIntelligenceFeature,
    WorkItemFormPerfConstants,
    WorkItemWatermarkAllAction
} from "Wit/WorkItemModel/OM";
import {
    FieldStatusFlags,
    IField,
    IFieldDefinition,
    IFieldRule,
    IFieldSetValueOptions,
    IFieldUpdate,
    IFieldsToEvaluate,
    ILinkUpdateResult,
    ILinkUpdates,
    IWorkItem,
    IWorkItemBulkSaveResult,
    IWorkItemCommentUpdate,
    IWorkItemError,
    IWorkItemInfoText,
    IWorkItemNodeData,
    IWorkItemRelatedData,
    IWorkItemTypeExtension,
    IWorkItemUpdate,
    IWorkItemUpdatePackage,
    IWorkItemUpdateResult,
    LinkUpdateResult,
    WorkItemChangeType,
    WorkItemDiscussionFormat
} from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import * as Resources from "Wit/WorkItemModel/Resources";
import { RuleEngine } from "Wit/WorkItemModel/RuleEngine/RuleEngine";
import { IWorkItemEventService, IWorkItemEventType } from "Wit/WorkItemModel/Services/IWorkItemEventService";
import { IWorkItemSavingService } from "Wit/WorkItemModel/Services/WorkItemSavingService";
import {
    getEndingNumberFromUrl,
    getErrorMessage,
    htmlSanitizerPreserveClassAndMentionOptions,
    isHtmlVisuallyBlank,
    workItemLinkBeginningSign
} from "Wit/WorkItemModel/Utils";
import {
    compareFieldValues,
    convertFieldValueToExternal,
    convertFieldValueToInternal,
    getFieldErrorText,
    isEmpty,
    isFieldValid,
    isIdentityPickerSupportedForField,
    isLongTextField,
    resetField,
    subtract,
    tryNormalizeFieldValue
} from "Wit/WorkItemModel/WorkItem/FieldUtils";
import { Attachment, ExternalLink, Hyperlink, Link, WorkItemLink } from "Wit/WorkItemModel/WorkItem/Links";
import { getTriggerList } from "Wit/WorkItemModel/WorkItem/WorkItemType";
import { filterXSS } from "xss";
import { FieldValidationKey } from "../RuleEngine/FieldValidationKey";
import { ServerDefaultValue } from "../RuleEngine/ServerDefaultValue";
import { WorkItemStore } from "../RuleEngine/WorkItemStore";
import { IWorkItemFieldEventService, IWorkItemFieldEventType } from "../Services/IWorkItemFieldEventService";
import { IWorkItemModelService } from "../Services/IWorkItemModelService";
import { isCloneable } from "./FieldDefinitionUtils";
import { Project } from "./Project";
import { WorkItemType } from "./WorkItemType";

export class WorkItem implements IWorkItem {
    private static tempIdSeed = 0;

    private static FIELD_ID_TO_DEPENDENT_ID_MAP: { [id: number]: number[] } = {
        [DalFields.AttachedFiles]: [CoreField.AttachedFileCount],
        [DalFields.RelatedLinks]: [CoreField.RelatedLinkCount, CoreField.Parent],
        [DalFields.LinkedFiles]: [CoreField.HyperLinkCount],
        [DalFields.BISURI]: [CoreField.ExternalLinkCount],
        [DalFields.HistoryId]: [CoreField.CommentCount]
    };

    public static getTempId(): number {
        return --WorkItem.tempIdSeed;
    }

    public static reserveTempId(tempId: number) {
        if (WorkItem.tempIdSeed > tempId) {
            WorkItem.tempIdSeed = tempId;
        }
    }

    public store: WorkItemStore;
    public id: number = 0;
    public tempId: number = 0;
    public revision: ObservableValue<number> = new ObservableValue<number>(0);
    public sessionId: string; // Used for tracking users behavior

    // TODO:Replat These should maybe be getters instead of being public
    public manualDirty: IObservableValue<boolean> = new ObservableValue<boolean>(false);
    public dirty: IObservableValue<boolean> = new ObservableValue<boolean>(false);
    public saving: IObservableValue<boolean> = new ObservableValue<boolean>(false);
    public infoText: IObservableValue<IWorkItemInfoText> = new ObservableValue<IWorkItemInfoText>({ invalid: false, text: "" });

    private promisesToWait: Map<number, Promise<any>> = new Map();
    private promiseToWaitId: number = 0;

    public referencedPersons: { [field: string]: WorkItemIdentityRef };
    public relatedData: { [field: string]: IWorkItemRelatedData };
    public extensions: IWorkItemTypeExtension[];

    public project: Project;
    public fieldMap: { [key: string]: IField };
    public fieldMapById: { [id: number]: IField };
    public fields: IField[];
    public initialValues: Map<number, IFieldUpdate>;
    public fieldUpdates: Map<number, IFieldUpdate>;
    public manuallySetFields: Map<number, any> = new Map<number, any>();
    public workItemType: WorkItemType;

    private _pageContext: IVssPageContext;
    private _workItemFormSavingService: IWorkItemSavingService;
    private _workItemEventService: IWorkItemEventService;
    private _workItemFieldEventService: IWorkItemFieldEventService;
    private _ruleEngine?: RuleEngine;
    private _perfService: IVssPerformanceService;

    private _allLinks: Link[];
    private _fieldData: IFieldDataDictionary;
    // Used for initialization and when work items change type
    private _fieldLookup: IFieldLookup;
    private _currentLinks?: Link[];
    private _revisions: any;
    private _typeData: IWorkItemTypeData;
    private _originalTypeData: IWorkItemTypeData;
    private _projectData: IProjectDataWithNodes;
    private _originalProjectData: IProjectDataWithNodes;
    private _fieldWatermark = new ObservableValue<number>(0);

    private _error?: IWorkItemError;
    private _eventsEnabled: boolean;
    private _loadTime: Date;
    private _revisionsPopulated: boolean;
    private _isCommentingAvailable: boolean;
    private _isReadOnly: boolean;
    private _isChangeProjectInProgress = false;
    private _fieldValidationMap: { [key: string]: boolean };
    private _originalExtensions: IWorkItemTypeExtension[];
    private _contributionErrorMap: { [field: string]: string };
    private _isMarkdownDiscussionEnabled: boolean;
    private _historyLinksOptimizationsEnabled: boolean;
    private _workItemFieldChangedEventOptimizationEnabled: boolean;

    private _previousStateName: string;

    public constructor(
        pageContext: IVssPageContext,
        typeData: IWorkItemTypeData,
        fieldProjectData: IFieldProjectData,
        nodeData: IWorkItemNodeData,
        constantSets: { [key: string]: string[] },
        data?: Partial<IWorkItemData>,
        extensions?: IWorkItemTypeExtension[],
        project?: Project
    ) {
        this._workItemFormSavingService = pageContext.getService<IWorkItemSavingService>("IWorkItemSavingService");
        this._workItemEventService = pageContext.getService<IWorkItemEventService>("IWorkItemEventService");
        this._workItemFieldEventService = pageContext.getService<IWorkItemFieldEventService>("IWorkItemFieldEventService");
        this._perfService = pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this.extensions = extensions || [];
        this._originalExtensions = this.extensions;
        this._contributionErrorMap = {};

        this._typeData = typeData;
        this._originalTypeData = this._typeData;

        // If a project is passed in, use it. Otherwise, use the old logic and create a new project for every work item
        if (project) {
            this.project = project;
            this._projectData = project.projectData;
            this._originalProjectData = this._projectData;
        } else {
            this._projectData = {
                ...fieldProjectData.projects[0],
                areaNodes: nodeData.areaPathNodes,
                iterationNodes: nodeData.iterationPathNodes
            };

            this._originalProjectData = this._projectData;
            this.project = new Project(this._projectData);
        }

        this._fieldLookup = {};
        fieldProjectData.fields.forEach(f => {
            this._fieldLookup[f.id] = f;
            this._fieldLookup[f.referenceName] = f;
        });

        this._pageContext = pageContext;

        // this.events = new Events_Handlers.NamedEventCollection();
        this._eventsEnabled = false;
        this._initializeFields(typeData);
        this._defineExtensionFields();

        this._isMarkdownDiscussionEnabled = isFeatureFlagEnabled(this._pageContext, FeatureFlags.IsMarkdownDiscussionEnabled, false);
        this._historyLinksOptimizationsEnabled = isFeatureFlagEnabled(this._pageContext, FeatureFlags.HistoryLinksOptimizationsEnabled, false);
        this._workItemFieldChangedEventOptimizationEnabled = isFeatureFlagEnabled(
            this._pageContext,
            FeatureFlags.WorkItemFieldChangedEventOptimizationEnabled,
            false
        );

        this._eventsEnabled = true;
        this.relatedData = {};
        this.sessionId = newGuid();
        this.saving = new ObservableValue<boolean>(false);
        this.store = new WorkItemStore(pageContext, constantSets);

        this.workItemType = new WorkItemType(pageContext, this._typeData, this.project, this._fieldLookup);

        let areaPathMru = undefined;
        let iterationPathMru = undefined;

        // Check to make sure MRU values are still valid
        if (nodeData.areaPathMru && this.project.getNodeById(nodeData.areaPathMru)) {
            areaPathMru = nodeData.areaPathMru;
        }

        if (nodeData.iterationPathMru && this.project.getNodeById(nodeData.iterationPathMru)) {
            iterationPathMru = nodeData.iterationPathMru;
        }

        // Load the data into the work item and do not reset the fields since no updates have been made yet.
        this.load(data, false, false, areaPathMru, iterationPathMru);
    }

    public hasStateChanged(): boolean {
        return this._previousStateName && this._previousStateName !== this.getFieldValueById(CoreField.State) ? true : false;
    }

    private fireWorkItemErrorChanged() {
        const updatedFieldIds: number[] = [];
        if (this._error) {
            const fieldId = this.fieldMap[this._error.fieldReferenceName].fieldDefinition.id;
            updatedFieldIds.push(fieldId);
        }
        // Trigger workItem change in any case (even if updateFieldIds is empty - in case of removing error message)
        this._onWorkItemChanged(updatedFieldIds, { workItemChangeType: WorkItemChangeType.ErrorChanged });
        this._workItemEventService.fireEvent({ workItem: this }, IWorkItemEventType.WorkItemErrorUpdated);
    }

    public setContributionErrorStatus(contributionInstanceId: string, isValid: boolean, errorMessage?: string) {
        if (isValid && this._contributionErrorMap.hasOwnProperty(contributionInstanceId)) {
            delete this._contributionErrorMap[contributionInstanceId];
            this.fireWorkItemErrorChanged();
        } else if (!isValid) {
            const prevError = this._contributionErrorMap[contributionInstanceId];
            this._contributionErrorMap[contributionInstanceId] = errorMessage || "";
            if (prevError !== this._contributionErrorMap[contributionInstanceId]) {
                this.fireWorkItemErrorChanged();
            }
        }
    }

    // TODO: Make this async
    public beginValidate(
        fieldId: number,
        fieldValidationKey: FieldValidationKey,
        callback: () => void,
        errorCallback: (error: Error, referenceName?: string) => void
    ): void {
        if (this.dirty.value && !this.isValidating(fieldValidationKey) && !this.saving.value) {
            const workItemUpdate = this.getUpdateData();

            if (workItemUpdate && workItemUpdate.payload) {
                this._setValidatingStatus(true, fieldValidationKey);
                const field = this.getFieldById(fieldId);

                this.workItemType.beginGetDependentFields(
                    fieldId,
                    this,
                    async (dependentFields: IFieldDefinition[]): Promise<void> => {
                        const fieldsToBeValidated: IFieldDefinition[] = dependentFields.concat(field!.fieldDefinition);
                        const originalFieldValues: { [key: string]: any } = {};
                        const newFieldValues: { [key: string]: any } = {};
                        fieldsToBeValidated.forEach((f: IFieldDefinition): void => {
                            originalFieldValues[f.referenceName] = this.getFieldValueById(
                                f.id,
                                true,
                                undefined,
                                undefined,
                                /* convertToExternal */ true
                            );
                            if (this.fieldUpdates.has(f.id)) {
                                newFieldValues[f.referenceName] = this.getFieldValueById(
                                    f.id,
                                    false,
                                    undefined,
                                    undefined,
                                    /* convertToExternal */ true
                                );
                            }
                        });

                        try {
                            const result = await this._pageContext
                                .getService<IVssContributionService>("IVssContributionService")
                                .getDataAsync<any>("ms.vss-work-web.work-item-rule-engine-data-provider", {
                                    fieldsToEvaluate: {
                                        projectId: this.project.guid,
                                        workItemType: this.workItemType.name, // Note - reference name should work but no incentive to change in fear of breaking on-prem
                                        fields: [field!.fieldDefinition.referenceName],
                                        fieldValues: originalFieldValues,
                                        fieldUpdates: newFieldValues
                                    } as IFieldsToEvaluate
                                });
                            if (result && result.exception) {
                                // rule engine validation failed.
                                if (errorCallback) {
                                    // If the identity is resolved successfully RuleValidationException will be thrown
                                    // If the identity is not resolved successfully WorkItemFieldInvalidException will be thrown
                                    // the property name for reference name of the field in the object model of exception are different
                                    const fieldReferenceName = result.exception.customProperties
                                        ? result.exception.customProperties.FieldReferenceName || result.exception.customProperties.ReferenceName
                                        : undefined;

                                    errorCallback(result.exception, fieldReferenceName);
                                }
                            } else {
                                // rule engine validation passed.
                                if (this.isValidating(fieldValidationKey) && !this.saving.value) {
                                    callback();
                                }
                            }
                        } catch (error) {
                            // this is not validation failure but other reason like network error, no field name can be provided
                            errorCallback(error);
                        } finally {
                            this._setValidatingStatus(false, fieldValidationKey);
                        }
                    },
                    (error?: string) => {
                        console.error(error);
                    }
                );
            }
        }
    }

    public isValidating(fieldValidationKey: FieldValidationKey): boolean {
        if (fieldValidationKey) {
            return this._fieldValidationMap[fieldValidationKey.toString()] || false;
        } else {
            for (const key in this._fieldValidationMap) {
                if (this._fieldValidationMap[key]) {
                    return true;
                }
            }
            return false;
        }
    }

    public validateIdentityFieldValue(fieldId: number) {
        const validatedValue = this.getFieldValueById(fieldId);
        const field = this.fieldMapById[fieldId];
        const validationKey = new FieldValidationKey(fieldId, validatedValue, field.filterByScope!);
        if (isIdentityPickerSupportedForField(field) && isFieldValid(field) && !(validatedValue instanceof ServerDefaultValue)) {
            // we dont want to run ruleengine on the field if its value is set by a serverdefaultvalue rule, as the rule engine API doesnt understand ServerDefault Rule
            this.beginValidate(
                fieldId,
                validationKey,
                () => {
                    // if the request comes back, but the current value or scope is changed for the field compared with those when request sent,
                    // we just ignore the result
                    // the response for the perticular scope and value will handle, since every value change or scope change will trigger eager validation
                    if (new FieldValidationKey(fieldId, this.getFieldValueById(fieldId), field.filterByScope!).equals(validationKey)) {
                        field.error = "";
                        this._onWorkItemChanged([fieldId], { workItemChangeType: WorkItemChangeType.ValidationCompleted });
                    }
                },
                (error, referenceName?: string) => {
                    let validatedField: IField = field;
                    if (referenceName) {
                        validatedField = this.getFieldByName(referenceName)!;
                    }

                    // If when we get the validation failure the field is already
                    // invalid then just let it be
                    if (
                        isFieldValid(validatedField) &&
                        new FieldValidationKey(fieldId, this.getFieldValueById(fieldId), field.filterByScope!).equals(validationKey)
                    ) {
                        validatedField.error = error.message;
                        validatedField.status = FieldStatusFlags.InvalidIdentityField;
                        this._onWorkItemChanged([fieldId], { workItemChangeType: WorkItemChangeType.Validating });
                        // TODO:Replat
                        // this.fireFieldChange([validatedField.fieldDefinition.id]);
                    }
                }
            );
        }
    }

    private _setValidatingStatus(validating: boolean, fieldValidationKey?: FieldValidationKey) {
        let fireEvent = false;
        if (fieldValidationKey) {
            if (this._fieldValidationMap[fieldValidationKey.toString()] !== validating) {
                this._fieldValidationMap[fieldValidationKey.toString()] = validating;
                fireEvent = true;
            }
        } else {
            for (const key in this._fieldValidationMap) {
                if (this._fieldValidationMap[key] !== validating) {
                    this._fieldValidationMap[key] = validating;
                    fireEvent = true;
                }
            }
        }

        if (!fireEvent) {
            return;
        }

        if (validating) {
            // TODO:Replat
            // this.fireWorkItemValidating();
        } else {
            // TODO:Replat
            // this.fireWorkItemValidationComplete();
        }
    }

    private _initializeFields(typeData: IWorkItemTypeData) {
        this.fields = [];
        this.fieldMap = {};
        this.fieldMapById = {};
        this._fieldValidationMap = {};

        typeData.fields.forEach((fieldId: number) => {
            this._defineFieldById(fieldId);
        });
    }

    public save(pathName?: string, keepDeletedLinks?: boolean): Promise<IWorkItemBulkSaveResult[]> {
        if (this.isReadOnly()) {
            throw new Error(Resources.WorkItemIsReadOnlyError);
        }

        let numberOfTags: number | undefined;
        let numberOfTagsDelta: number | undefined;

        const updatedTagsValue = this._splitTagNames(this.getFieldValueById(DalFields.Tags));

        const originalTagsValue = this._splitTagNames(this.getFieldValueById(DalFields.Tags, true));

        if (originalTagsValue !== updatedTagsValue) {
            numberOfTagsDelta = updatedTagsValue.length - originalTagsValue.length;
            const updatedTagsDelta = subtract(updatedTagsValue, originalTagsValue);

            this._pageContext.getService<IWorkItemModelService>("IWorkItemModelService").addTagsMru(updatedTagsDelta);
        }

        const workItemSavePerfScenario = this._perfService.startScenario("WorkItem.Save", false);

        if (pathName == null) {
            // set the pathname to current URL to know the unknown paths, if we are missing any.
            pathName = window.location.pathname;
        }

        workItemSavePerfScenario.addProperties({
            workItemType: "[NonEmail: " + this._typeData.referenceName + "]",
            numberOfTags,
            numberOfTagsDelta,
            PathName: pathName
        });

        if (this.isNew()) {
            this.saveLastAreaAndIteration();
        }

        if (this._isMarkdownDiscussionEnabled && this.fieldUpdates.has(CoreField.History)) {
            const addedComment = this.fieldUpdates?.get(CoreField.History);
            if (addedComment) {
                const settingsService = this._pageContext.getService<ISettingsService>("ISettingsService");
                const formatSettings = settingsService.getEntry<string>("WorkItemDiscussionSettings/CommentFormatState", SettingsUserScope.Me);
                const isMarkdown = formatSettings === WorkItemDiscussionFormat.Markdown;
                if (isMarkdown) {
                    addedComment.value = filterXSS(
                        translateDisplayNamesToStorageKeys(this._pageContext, addedComment.value),
                        htmlSanitizerPreserveClassAndMentionOptions
                    );
                }
                this.addLinksForNewMentionsInComment(addedComment.value, isMarkdown);
                const commentUpdate: IWorkItemCommentUpdate = {
                    // If Markdown FF is enabled and format settings is not defined then it means that the user has not changed the format settings therefore HTML is the default format.
                    format: formatSettings ? formatSettings : WorkItemDiscussionFormat.Html,
                    text: addedComment.value
                };

                addedComment.value = commentUpdate;
            }
        }

        return new Promise((resolve, reject) =>
            this._workItemFormSavingService.beginSaveWorkItemsBatch(
                [this],
                successResult => {
                    this._perfService.addSplitTiming("WorkItem.Save.Complete", workItemSavePerfScenario.scenarioName);

                    resolve(successResult);

                    this._perfService.endScenario(WITCommonConstants.WorkItemTrackingArea, workItemSavePerfScenario.scenarioName, false);
                },
                error => {
                    this._perfService.endScenario(WITCommonConstants.WorkItemTrackingArea, workItemSavePerfScenario.scenarioName, false);

                    if (error && error.name === Exceptions.WorkItemBulkSaveException) {
                        error = error.results[0].error;
                    }

                    reject(error);
                },
                keepDeletedLinks
            )
        );
    }

    private saveLastAreaAndIteration() {
        const areaId = this.getFieldValueByName(CoreFieldRefNames.AreaId);
        const iterationId = this.getFieldValueByName(CoreFieldRefNames.IterationId);
        const nodeService = this._pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");
        nodeService.updateMruAreaPaths(this.project.guid, [areaId]);
        nodeService.updateMruIterations(this.project.guid, [iterationId]);
    }

    private saveLinks(links: WorkItemLink[], fieldsToClear?: CoreField[]): Promise<IWorkItemBulkSaveResult[]> {
        return this._workItemFormSavingService.saveWorkItemLinksAsync(this, links, fieldsToClear);
    }

    public isReadOnly(): boolean {
        return this._isReadOnly || this.isDeleted();
    }

    public isCommentingAvailable(): boolean {
        return !this.isDeleted() && this._isCommentingAvailable;
    }

    public isDeleted(): boolean {
        return this.getFieldValueByName(CoreFieldRefNames.IsDeleted);
    }

    public isValid(): boolean {
        const invalidFields = this.getInvalidFields(true);

        return (!invalidFields || invalidFields.length === 0) && Object.keys(this._contributionErrorMap).length === 0;
    }

    public isNew(): boolean {
        return this.id === undefined || this.id < 1;
    }

    public hasWorkItemTypeChanged(): boolean {
        return this._originalTypeData !== this._typeData;
    }

    public hasTeamProjectChanged(): boolean {
        return this._originalProjectData !== this._projectData;
    }

    public getError(): IWorkItemError | undefined {
        return this._error;
    }

    public setError(error: Error): void {
        // IMPORTANT: DO NOT try copy object created by Error() unless you know how to - a couple properties are not enumerable and get lost.
        this._error = error as IWorkItemError;
        this.updateInfoText();
        this._workItemEventService.fireEvent({ workItem: this }, IWorkItemEventType.WorkItemErrorUpdated);
    }

    public clearError(): void {
        if (this._error) {
            this._error = undefined;
        }
    }

    public getInvalidFields(stopAtFirst?: boolean): IField[] {
        const result: IField[] = [];
        const fields = this.fields;
        for (const field of fields) {
            if (!isFieldValid(field)) {
                result.push(field);
                if (stopAtFirst) {
                    break;
                }
            }
        }

        return result;
    }

    public getDirtyFields(onlyUserChanges?: boolean): IField[] {
        const result: IField[] = [];

        for (const field of this.fields) {
            const fieldId = field.fieldDefinition.id;

            if (onlyUserChanges && this.manuallySetFields?.has(fieldId)) {
                result.push(field);
            } else if (!onlyUserChanges && this.fieldUpdates?.has(fieldId)) {
                result.push(field);
            }
        }

        return result;
    }

    public isFieldDirty(fieldNameOrId: number | string): boolean {
        let field: IField | undefined;
        if (typeof fieldNameOrId === "string") {
            field = this.getFieldByName(fieldNameOrId);
        } else if (typeof fieldNameOrId === "number") {
            field = this.getFieldById(fieldNameOrId);
        }
        if (field) {
            return this.fieldUpdates.has(field.fieldDefinition.id);
        }

        return false;
    }

    private updateInfoText() {
        const result: IWorkItemInfoText = { text: "", invalid: false };
        const invalidFields = this.getInvalidFields(true);

        // If the work item has invalid fields, display the error for the field.
        if (invalidFields && invalidFields.length > 0) {
            result.text = getFieldErrorText(invalidFields[0], this.getFieldValueById(invalidFields[0].fieldDefinition.id));
            result.invalid = true;
        } else if (this._error) {
            if (this._error.fieldReferenceName && !this.getFieldByName(this._error.fieldReferenceName.toUpperCase())) {
                // The bad field doesn't exist on the work item.
                // Most likely, the process has been recently customized, and the browser needs to be refreshed.
                result.text = Resources.BrowserRefreshRequired;
            } else {
                // The work item has an error associated with it, so display the error.
                result.text = getErrorMessage(this.getError()!);
            }

            result.invalid = true;
        } else if (this._contributionErrorMap && Object.keys(this._contributionErrorMap).length > 0) {
            result.invalid = true;
            result.text = this._contributionErrorMap[Object.keys(this._contributionErrorMap)[0]] || "";
        } else {
            result.text = "";
        }

        if (this.infoText.value.text !== result.text || this.infoText.value.invalid !== result.invalid) {
            this.infoText.value = result;
        }
    }

    public getTitle(): string {
        return this.getFieldValueById(CoreField.Title) || "";
    }

    public getCaption(options?: { excludeTempId?: boolean; includeDirtyModifier?: boolean }): string {
        let caption: string;
        if (this.isNew()) {
            const tempId = options?.excludeTempId ? "" : -this.tempId;
            caption = format(Resources.WorkItemCaptionNew, this._typeData.name, tempId);
        } else {
            caption = `${this._typeData.name} ${this.id}`;
        }

        if (options?.includeDirtyModifier && this.dirty.value) {
            caption = `${caption}*`;
        }

        return caption;
    }

    public getUniqueId(): number {
        return this.id || this.tempId;
    }

    public getTypeData(): IWorkItemTypeData {
        return this._typeData;
    }

    private _getCommentCount(original?: boolean): number {
        let commentCount = this._fieldData[CoreField.CommentCount] || 0;
        if (!original && this.fieldUpdates.has(CoreField.History)) {
            commentCount += 1;
        }

        return commentCount;
    }

    //#region Field getting and setting

    public getFieldLookup(): IFieldLookup {
        return this._fieldLookup;
    }

    public getFieldByName(fieldName: string): IField | undefined {
        return this.fieldMap[fieldName.toUpperCase()];
    }

    public getFieldById(fieldId: number): IField | undefined {
        return this.fieldMapById[fieldId];
    }

    public getFieldValueById(
        fieldId: number,
        original?: boolean,
        omitSetByRule?: boolean,
        asIdentityRef?: boolean,
        convertToExternal?: boolean
    ): any {
        const field = this.getFieldById(fieldId);

        if (!field) {
            return undefined;
        }

        return this._getFieldValue(field.fieldDefinition, original, omitSetByRule, asIdentityRef, convertToExternal);
    }

    public getFieldValueByName(
        fieldName: string,
        original?: boolean,
        omitSetByRule?: boolean,
        asIdentityRef?: boolean,
        convertToExternal?: boolean
    ): any {
        const field = this.getFieldByName(fieldName);

        if (!field) {
            return undefined;
        }

        return this._getFieldValue(field.fieldDefinition, original, omitSetByRule, asIdentityRef, convertToExternal);
    }

    private _getFieldValue(
        fieldDefinition: IFieldDefinition,
        original?: boolean,
        omitSetByRule?: boolean,
        asIdentityRef?: boolean,
        convertToExternal?: boolean
    ): any {
        const updates = this.fieldUpdates;
        const fieldId = fieldDefinition.id;

        switch (fieldId) {
            case CoreField.AttachedFileCount:
                return this._getLinkCount(Attachment, !!original);
            case CoreField.ExternalLinkCount:
                return this._getLinkCount(ExternalLink, !!original);
            case CoreField.HyperLinkCount:
                return this._getLinkCount(Hyperlink, !!original);
            case CoreField.RelatedLinkCount:
                return this._getLinkCount(WorkItemLink, !!original);
            case CoreField.Parent:
                return this._getParentLinkId(!!original);
            case CoreField.CommentCount:
                return this._getCommentCount(original);
            case CoreField.TeamProject:
                if (this.isNew() && this._projectData) {
                    // For new workitems, return the current project.
                    // This is to support any defaulting rules based off TeamProject value
                    return this._projectData.name;
                }
        }

        if (!original && updates.has(fieldId)) {
            const update: IFieldUpdate = updates.get(fieldId)!;
            if (!update.setByRule || !omitSetByRule) {
                return this.convertFieldValue(update.value, fieldDefinition, asIdentityRef);
            }
        }

        const fieldDataValue = this._fieldData[fieldId];
        const value = convertToExternal ? convertFieldValueToExternal(fieldDataValue, fieldDefinition.type) : fieldDataValue;

        return convertPotentialIdentityRefFromFieldValue(this._pageContext, value, asIdentityRef);
    }

    private convertFieldValue(value: any, fieldDefinition: IFieldDefinition, asIdentityRef?: boolean): any {
        return convertPotentialIdentityRefFromFieldValue(this._pageContext, convertFieldValueToExternal(value, fieldDefinition.type), asIdentityRef);
    }

    public setFieldValueByName(fieldName: string, value: any, options?: IFieldSetValueOptions): boolean {
        const field = this.getFieldByName(fieldName);

        return this.wrappedSetFieldValue(field, value, options);
    }

    public setFieldValueById(fieldId: number, value: any, options?: IFieldSetValueOptions): boolean {
        const field = this.getFieldById(fieldId);

        return this.wrappedSetFieldValue(field, value, options);
    }

    private wrappedSetFieldValue(field: IField | undefined, value: any, options?: IFieldSetValueOptions): boolean {
        const setFieldValueDelegate = (): boolean => {
            if (this._workItemFieldChangedEventOptimizationEnabled) {
                if (!field) {
                    return false;
                }

                // if we get a work item identity ref, convert to the combo string
                // since write operations only support combo string
                if (isWorkItemIdentityRef(value)) {
                    value = value.distinctDisplayName;
                }

                const isDateParsingFixEnabled = !isFeatureFlagEnabled(this._pageContext, FeatureFlags.DateParsingFixDisabled, false);
                const newValue = convertFieldValueToInternal(tryNormalizeFieldValue(value, field), field.fieldDefinition.type, isDateParsingFixEnabled);

                if (options?.setByRule) {
                    return this._setFieldValueForce(field, newValue.value, options);
                } else {
                    return this.setFieldValue(field, newValue, options);
                }
            } else {
                return this._setFieldValue(field, value, options);
            }
        };

        if (!options?.handleExceptions) {
            return setFieldValueDelegate();
        }

        try {
            return setFieldValueDelegate();
        } catch (e) {
            let errorText = "An error occurred while bulk-editing a work item";
            if (e instanceof Error) {
                errorText = errorText + `: ${e.message} Field Name: ${field?.fieldDefinition.name}, Value: ${value}`;
            }
            console.error(errorText);
            return false;
        }
    }

    private setFieldValue(field: IField | undefined, newValue: any, options?: IFieldSetValueOptions): boolean {
        if (!field) {
            return false;
        }
        options = {
            preventFire: false,
            fireIdentityEagerValidation: false,
            setByRule: false,
            ...options
        };

        const oldValue = this.getFieldValueById(field.fieldDefinition.id);

        this._setFieldValueForce(field, newValue.value, options);

        // if the field value has changed, or we force evaluate
        if (options.evaluate ?? !compareFieldValues(oldValue, newValue.value)) {
            // then with the field changed, we run the rule engine (evaluateField just calls _ruleEngine.evaluateField)
            // we get back the fields that were changed by the rules
            const changedFields = this.evaluateField(field.fieldDefinition.id, true, oldValue);

            field.status |= newValue.status;

            if (options.fireIdentityEagerValidation) {
                // if the field is identity field and no client side validation error occurred, then start eager validation from server
                this.validateIdentityFieldValue(field.fieldDefinition.id);
            }

            if (!options.preventFire) {
                // then we tell the work item which fields have changed
                this._onWorkItemChanged(changedFields, { setByRules: options.setByRule, workItemChangeType: WorkItemChangeType.FieldChange });
            }
        }

        return true;
    }

    /**
     * Force field value without any validation, rule evaluation and fire the WI change event
     */
    private _setFieldValueForce(field: IField | undefined, newValue: any, options?: IFieldSetValueOptions): boolean {
        let isReadOnly: boolean = this.isReadOnly();
        if (options?.checkIsCommentingAvailable) {
            const isHistoryField = field?.fieldDefinition?.id == CoreField.History;
            const ignoreReadOnly = isHistoryField && this.isCommentingAvailable();
            isReadOnly = !ignoreReadOnly && this._isReadOnly;
        }

        if (!field || isReadOnly) {
            return false;
        }

        options = {
            preventFire: false,
            fireIdentityEagerValidation: false,
            setByRule: false,
            ...options
        };

        const fieldId = field.fieldDefinition.id;
        const oldValue = this.getFieldValueById(fieldId);
        const originalValue = this._fieldData[fieldId];

        switch (fieldId) {
            case CoreField.AttachedFileCount:
            case CoreField.ExternalLinkCount:
            case CoreField.HyperLinkCount:
            case CoreField.RelatedLinkCount:
            case CoreField.Parent:
            case CoreField.CommentCount:
                return false;
            case CoreField.AreaId:
            case CoreField.IterationId:
                // TODO:Replat Referenced node stuff
                if (!this.project.getNodeById(newValue as number)) {
                    throw new Error("Invalid node id.");
                }
                break;
            case CoreField.Rev:
                this.revision.value = newValue as number;
                break;
            case CoreField.State:
                //when evaluating field rules it's being updated twice with the same value
                if (oldValue !== newValue) {
                    this._previousStateName = oldValue;
                }
        }

        const isIdentityField = field.fieldDefinition.isIdentity;

        // A change was made to the history field
        const historyChange = fieldId === CoreField.History;

        // The history field was changed to a non-empty value
        const nonEmptyHistoryChange = historyChange && !compareFieldValues("", newValue);

        // Another field's value was changed
        const otherFieldChange = !historyChange && !compareFieldValues(originalValue, newValue, undefined, isIdentityField);

        if (nonEmptyHistoryChange || otherFieldChange) {
            const currentUpdate = this.fieldUpdates.get(fieldId);
            if (currentUpdate && currentUpdate.value === newValue) {
                // If it is the same value, AND setByRule to see if either change was done by the user
                this.fieldUpdates.set(fieldId, { value: newValue, setByRule: currentUpdate.setByRule && options.setByRule });
            } else {
                this.fieldUpdates.set(fieldId, { value: newValue, setByRule: options.setByRule });
            }
            if (!options.setByRule) {
                this.manuallySetFields.set(fieldId, newValue);
            }
            if (nonEmptyHistoryChange) {
                // Evaluate CommentCount-dependent rules for non-empty history changes
                const commentCountValue = this.getFieldValueById(CoreField.CommentCount);
                this.evaluateField(CoreField.CommentCount, true, commentCountValue);
            }
        } else {
            this.fieldUpdates.delete(fieldId);
            if (this.manuallySetFields) {
                this.manuallySetFields.delete(fieldId);
            }
        }
        this.evaluateDirty();

        return true;
    }

    private _setFieldValue(field: IField | undefined, value: any, options?: IFieldSetValueOptions): boolean {
        let isReadOnly: boolean = this.isReadOnly();
        if (options?.checkIsCommentingAvailable) {
            const isHistoryField = field?.fieldDefinition?.id == CoreField.History;
            const ignoreReadOnly = isHistoryField && this.isCommentingAvailable();
            isReadOnly = !ignoreReadOnly && this._isReadOnly;
        }

        if (isReadOnly) {
            return false;
        }
        if (field) {
            options = {
                preventFire: false,
                fireIdentityEagerValidation: false,
                setByRule: false,
                ...options
            };

            // if we get a work item identity ref, convert to the combo string
            // since write operations only support combo string
            if (isWorkItemIdentityRef(value)) {
                value = value.distinctDisplayName;
            }

            const fieldId = field.fieldDefinition.id;

            const isDateParsingFixEnabled = !isFeatureFlagEnabled(this._pageContext, FeatureFlags.DateParsingFixDisabled, false);

            const oldValue = this.getFieldValueById(fieldId);
            const newValue = convertFieldValueToInternal(
                tryNormalizeFieldValue(value, field),
                field.fieldDefinition.type,
                isDateParsingFixEnabled
            );
            const originalValue = this._fieldData[fieldId];

            switch (fieldId) {
                case CoreField.AttachedFileCount:
                case CoreField.ExternalLinkCount:
                case CoreField.HyperLinkCount:
                case CoreField.RelatedLinkCount:
                case CoreField.Parent:
                case CoreField.CommentCount:
                    return false;
                case CoreField.AreaId:
                case CoreField.IterationId:
                    // TODO:Replat Referenced node stuff
                    if (!this.project.getNodeById(value as number)) {
                        throw new Error("Invalid node id.");
                    }
                    break;
                case CoreField.Rev:
                    this.revision.value = newValue.value as number;
                    break;
                case CoreField.State:
                    //when evaluating field rules it's being updated twice with the same value
                    if (oldValue !== newValue.value) {
                        this._previousStateName = oldValue;
                    }
            }

            const isIdentityField = field.fieldDefinition.isIdentity;

            // A change was made to the history field
            const historyChange = fieldId === CoreField.History;

            // The history field was changed to a non-empty value
            const nonEmptyHistoryChange = historyChange && !compareFieldValues("", newValue.value);

            // Another field's value was changed
            const otherFieldChange =
                !historyChange && !compareFieldValues(originalValue, newValue.value, undefined, isIdentityField);

            if (nonEmptyHistoryChange || otherFieldChange) {
                const currentUpdate = this.fieldUpdates.get(fieldId);
                if (currentUpdate && currentUpdate.value === newValue.value) {
                    // If it is the same value, AND setByRule to see if either change was done by the user
                    this.fieldUpdates.set(fieldId, { value: newValue.value, setByRule: currentUpdate.setByRule && options.setByRule });
                } else {
                    this.fieldUpdates.set(fieldId, { value: newValue.value, setByRule: options.setByRule });
                }
                if (!options.setByRule) {
                    this.manuallySetFields.set(fieldId, newValue.value);
                }
                if (nonEmptyHistoryChange) {
                    // Evaluate CommentCount-dependent rules for non-empty history changes
                    const commentCountValue = this.getFieldValueById(CoreField.CommentCount);
                    this.evaluateField(CoreField.CommentCount, true, commentCountValue);
                }
            } else {
                this.fieldUpdates.delete(fieldId);
                if (this.manuallySetFields) {
                    this.manuallySetFields.delete(fieldId);
                }
            }
            this.evaluateDirty();

            // if the field value has changed, or we force evaluate
            if (options.evaluate ?? !compareFieldValues(oldValue, newValue.value)) {
                // then with the field changed, we run the rule engine (evaluateField just calls _ruleEngine.evaluateField)
                // we get back the fields that were changed by the rules
                const changedFields = this.evaluateField(field.fieldDefinition.id, true, oldValue);

                field.status |= newValue.status;

                if (options.fireIdentityEagerValidation) {
                    // if the field is identity field and no client side validation error occurred, then start eager validation from server
                    this.validateIdentityFieldValue(field.fieldDefinition.id);
                }

                if (!options.preventFire) {
                    // then we tell the work item which fields have changed
                    this._onWorkItemChanged(changedFields, { setByRules: options.setByRule, workItemChangeType: WorkItemChangeType.FieldChange });
                }
            }
            return true;
        } else {
            return false;
        }
    }

    public evaluateField(fieldId: number, valueLocked?: boolean, prevValue?: any): number[] {
        return this._getRuleEngine().evaluateField(fieldId, valueLocked, prevValue);
    }

    private _defineFieldById(fieldId: number) {
        if (!this.fieldMapById.hasOwnProperty(fieldId) && this._fieldLookup[fieldId]) {
            this._defineField(this._fieldLookup[fieldId]!);
        }
    }

    private _defineField(fieldDefinition: IFieldEntry) {
        const fieldDef: IFieldDefinition = {
            ...fieldDefinition,
            rules: this._typeData.rules.fieldRules ? this._typeData.rules.fieldRules[fieldDefinition.id] : [],
            workItemType: { ...this._typeData, orderedStates: this._typeData.orderedStates ?? [] }
        };

        const field = { fieldDefinition: fieldDef, status: FieldStatusFlags.None, workItem: this, patterns: [] };

        this.fieldMap[fieldDef.referenceName.toUpperCase()] = field;
        this.fieldMapById[fieldDef.id] = field;

        this.fields.push(field);
    }

    public getFieldUpdates(): IFieldDataDictionary {
        const fields: IFieldDataDictionary = {};

        this.fieldUpdates.forEach((update, fieldId) => {
            const field = this.getFieldById(fieldId);
            if (field && field.fieldDefinition && field.fieldDefinition.flags & FieldFlags.Computed) {
                return;
            }
            let value = update.value;
            if (value === undefined) {
                value = null;
            }

            fields[fieldId] = value;
        });

        return fields;
    }

    public getFieldData(): IFieldDataDictionary {
        return this._fieldData;
    }

    //#endregion

    public evaluateDirty(): void {
        const dirtyEval = this.isDirty();
        if (this.dirty.value !== dirtyEval) {
            this.dirty.value = dirtyEval;
        }

        const manualDirtyEval = this.isDirty(true);
        if (this.manualDirty.value !== manualDirtyEval) {
            this.manualDirty.value = manualDirtyEval;
        }
    }

    /**
     * Checks the work item is dirty or not
     * @param onlyUserChanges A boolean flag indicating if it should only check for manually changed field values
     * @param ignoreFields An array of field ids (numeric ids) whose field value changes should be ignored
     * @param useIsCommentingAvailable Uses workItem's isCommentingAvailable permission value during readonly calculation
     */
    public isDirty(onlyUserChanges?: boolean, ignoreFields?: number[], useIsCommentingAvailable?: boolean): boolean {
        let isReadOnly: boolean = this.isReadOnly();
        if (useIsCommentingAvailable) {
            isReadOnly = !this._isCommentingAvailable && this.isReadOnly();
        }

        if (isReadOnly || this.isDeleted()) {
            return false;
        }

        if (this._fieldsDirty(onlyUserChanges, ignoreFields)) {
            return true;
        }

        if (this._linksDirty()) {
            return true;
        }

        return false;
    }

    private _linksDirty(): boolean {
        for (const link of this._allLinks) {
            // Don't check the dirty status of removed links
            if (link.isRemoved()) {
                continue;
            }

            // If the link is added by a system we dont want to make workitem dirty. Also if the link is updated we want to make it as dirty
            if (link.isNew() && link instanceof WorkItemLink && link.linkData.isAddedBySystem) {
                continue;
            }

            // If a link is going to be deleted, the links are dirty unless that link was newly added.
            // Likewise, if a link is new and not going to be deleted, the links are dirty.
            if (link.pendingDeletion !== link.isNew()) {
                return true;
            }

            // pendingUpdate is the actual dirty flag on the link
            if (link.pendingUpdate) {
                return true;
            }
        }
        return false;
    }

    /**
     * Check any field changed explicitly or not
     * @param onlyUserChanges A boolean flag indicating if it should only check for manually changed field values
     * @param ignoreFields An array of field ids (numeric ids) whose field value changes should be ignored
     */
    private _fieldsDirty(onlyUserChanges?: boolean, ignoreFields?: number[]): boolean {
        if (onlyUserChanges) {
            if (this.manuallySetFields) {
                const manuallySetFields_clone = new Map(this.manuallySetFields);
                if (ignoreFields && ignoreFields.length > 0) {
                    for (const ignoreFieldId of ignoreFields) {
                        manuallySetFields_clone.delete(ignoreFieldId);
                    }
                }
                return manuallySetFields_clone.size > 0;
            }
        } else if (this.isNew()) {
            return true;
        } else {
            const fieldUpdates_clone = new Map(this.fieldUpdates);
            if (ignoreFields && ignoreFields.length > 0) {
                for (const ignoreFieldId of ignoreFields) {
                    fieldUpdates_clone.delete(ignoreFieldId);
                }
            }
            for (const fieldId of fieldUpdates_clone.keys()) {
                if (!fieldUpdates_clone.get(fieldId)?.setByRule) {
                    return true;
                }
            }
        }

        return false;
    }

    private _onWorkItemChanged(changedFields: number[], options?: { setByRules?: boolean; workItemChangeType?: WorkItemChangeType }): void {
        this.clearError();
        this.updateInfoText();
        const uniqueChangedFields: Map<number, IField> = new Map();

        // For performance reasons we don't perform these operations if we are not firing events.
        // This is used during construction of work items since no events can be attached until
        // the object is constructed in any case.
        if (this._eventsEnabled && changedFields) {
            for (const fieldId of changedFields) {
                if (!uniqueChangedFields.has(fieldId)) {
                    const field = this.getFieldById(fieldId);

                    if (field) {
                        const fieldId = field.fieldDefinition.id;
                        const dependentFieldIds: number[] = WorkItem.FIELD_ID_TO_DEPENDENT_ID_MAP[fieldId];

                        uniqueChangedFields.set(fieldId, field);

                        if (dependentFieldIds) {
                            dependentFieldIds.forEach(dependentFieldId => {
                                const dependentField = this.getFieldById(dependentFieldId);
                                if (dependentField) {
                                    uniqueChangedFields.set(dependentFieldId, dependentField);
                                }
                            });
                        }
                    } else {
                        // else, we don't have a good field definition here for a field that has changed. This can be true for things like the isDeleted field, which seems to exist only after a work item has been created.
                        uniqueChangedFields.delete(fieldId);
                    }
                }
            }

            for (const [fieldId, field] of uniqueChangedFields.entries()) {
                this.updateFieldWatermark(fieldId.toString());
            }
        }

        this._workItemFieldEventService.fireEvent(
            {
                workItem: this,
                fieldDefinitionIds: Array.from(uniqueChangedFields.keys()),
                setByRules: options?.setByRules,
                workItemChangeType: options?.workItemChangeType
            },
            IWorkItemFieldEventType.WorkItemFieldsUpdated
        );

        this._workItemEventService.fireEvent({ workItem: this }, IWorkItemEventType.WorkItemUpdated);
    }

    public getEmptyUpdateData(): IWorkItemUpdate {
        const update: IWorkItemUpdate = this.prepareWorkItemUpdateData();

        update.payload = this.prepareWorkItemUpdatePackage();

        return update;
    }

    public getUpdateData(): IWorkItemUpdate {
        let hasUpdateData = false;

        const update: IWorkItemUpdate = this.prepareWorkItemUpdateData();

        const payload: IWorkItemUpdatePackage = this.prepareWorkItemUpdatePackage();

        const fields = this.getFieldUpdates();
        if (fields) {
            this.addLinksForNewMentions(fields);
            payload.fields = fields;

            // setting team project field explicitly as getFieldUpdates excludes the team project field as it's computed field
            if (this.hasTeamProjectChanged() && payload.fields) {
                payload.fields[CoreField.TeamProject] = this._projectData.name;
            }
            hasUpdateData = true;
        }

        const links = this.getLinkUpdates(update);
        if (links) {
            payload.links = links;
            hasUpdateData = true;
        }

        if (hasUpdateData) {
            update.payload = payload;
        }

        return update;
    }

    private prepareWorkItemUpdateData(): IWorkItemUpdate {
        return {
            uniqueId: this.getUniqueId(),
            id: this.id,
            tempId: this.tempId,
            rev: this.revision.value,
            projectId: this._projectData.guid,
            addedLinks: [],
            deletedLinks: [],
            updatedLinks: []
        };
    }

    private prepareWorkItemUpdatePackage(): IWorkItemUpdatePackage {
        return {
            id: this.id,
            rev: this.revision.value,
            projectId: this._projectData.guid,
            isDirty: this.isDirty(),
            tempId: this.isNew() ? this.tempId : undefined
        };
    }

    /**
     * When we save field updates that contain mentions in html fields, we
     * should automatically create links to those mentioned work items.
     */
    private addLinksForNewMentions(fieldUpdates: IFieldDataDictionary): void {
        const currentUserDisplayName = this._pageContext.getService<IVssPageService>("IVssPageService").getData()?.user?.displayName;
        if (!currentUserDisplayName) {
            // This is unlikely, but it should be fine to bail in this case.
            return;
        }

        const allMentionedWorkItemIds = new Set<number>();

        for (const [fieldId, newFieldValue] of Object.entries(fieldUpdates)) {
            const fieldType = this._fieldLookup[fieldId]?.type;
            if (fieldType !== FieldType.Html && fieldType !== FieldType.History) {
                // There cannot be mentions in a field that is not one of these types
                continue;
            }

            if (typeof newFieldValue !== "string") {
                continue;
            }
            this.findMentionsInHTMLFieldValue(newFieldValue, allMentionedWorkItemIds);
        }
        this.prepareAndAddRelatedlinks(allMentionedWorkItemIds, currentUserDisplayName);
    }

    private prepareAndAddRelatedlinks(allMentionedWorkItemIds: Set<number>, currentUserDisplayName: string): WorkItemLink[] {
        const newLinks = [...allMentionedWorkItemIds].map(id =>
            WorkItemLink.createSync(CoreLinkTypes.Related, id, format(Resources.LinkedFromMentionCommentFormat, currentUserDisplayName, id, this.id))
        );

        // Any duplicates will be skipped, so we do not have to worry about deduping.
        this.addLinks(newLinks);
        return newLinks;
    }

    private findMentionsInHTMLFieldValue(newFieldValue: string, allMentionedWorkItemIds: Set<number>) {
        const element = document.createElement("div");
        element.innerHTML = newFieldValue;
        const allMentionAnchors: NodeListOf<HTMLAnchorElement> = element.querySelectorAll("a[data-vss-mention]");
        allMentionAnchors.forEach(a => {
            if (!a.outerText.startsWith(workItemLinkBeginningSign)) {
                // Not work item mention
                return;
            }
            const id = getEndingNumberFromUrl(a.href);
            if (!id || (id && (!Number.isFinite(id) || id <= 0))) {
                // Bad mention - id is invalid
                return;
            }
            allMentionedWorkItemIds.add(Number(id));
        });
    }

    private async findMentionsInMarkdownFieldValue(newFieldValue: string): Promise<Set<number>> {
        const allMentionedWorkItemIds = new Set<number>();
        const idsToCheck: number[] = [];
        // checks if the hashtag only has numbers and adds value to allMentionedWorkItemIds if so
        const checkId = (value: string) => {
            const id = parseInt(value);
            if (Number.isFinite(id) || id > 0) {
                idsToCheck.push(id);
            }
        };

        const checkWorkItemsExist = async (workItemIds: number[]) => {
            const client = this._pageContext.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");
            const result = await client.getWorkItems(
                workItemIds,
                /*project*/ undefined,
                /*fields*/ [CoreFieldRefNames.Id],
                /*asOf*/ undefined,
                /*expand*/ undefined,
                /*errorPolicy*/ WorkItemErrorPolicy.Omit
            );
            if (result) {
                result.map(item => allMentionedWorkItemIds.add(item.id));
            }
        };

        const possibleMentions: string[] = newFieldValue.split(/(?<=\s|^)(#[0-9]+[^#\n]*)/g).filter(possibleMention => Boolean(possibleMention));

        for (const mention of possibleMentions) {
            if (!mention.startsWith(workItemLinkBeginningSign)) {
                continue;
            }

            const matches = mention.match(/#([0-9]+)/g);
            if (matches) {
                for (const match of matches) {
                    const id = match.slice(1);
                    checkId(id);
                }
            }
        }

        idsToCheck.length > 0 && (await checkWorkItemsExist(idsToCheck));

        return allMentionedWorkItemIds;
    }

    public async addLinksForNewMentionsInComment(comment: string, isMarkdown?: boolean) {
        const currentUserDisplayName = this._pageContext.getService<IVssPageService>("IVssPageService").getData()?.user?.displayName;
        if (!currentUserDisplayName) {
            // This is unlikely, but it should be fine to bail in this case.
            return;
        }
        let allMentionedWorkItemIds = new Set<number>();
        if (isMarkdown) {
            allMentionedWorkItemIds = await this.findMentionsInMarkdownFieldValue(comment);
        } else {
            this.findMentionsInHTMLFieldValue(comment, allMentionedWorkItemIds);
        }

        //do not create link for current wi
        if (allMentionedWorkItemIds.has(this.id)) {
            allMentionedWorkItemIds.delete(this.id);
        }

        if (allMentionedWorkItemIds.size > 0) {
            const preparedLinks = this.prepareAndAddRelatedlinks(allMentionedWorkItemIds, currentUserDisplayName);
            //after comment save we change revision value manually
            //and we also need to clear history field value because it contains current comment value and required to be cleaned
            //to avoid save button lock
            this.saveLinks(preparedLinks, [CoreField.History, CoreField.Rev]);
        }
    }

    public evaluate(alreadyChangedFields?: number[], updatedFieldIds?: number[]) {
        const fieldIdsToEval: number[] = [];
        const fieldIdMap: { [id: number]: boolean } = {};

        getTriggerList(this._typeData, (fieldId: number) => !!this.getFieldById(fieldId)).forEach((fieldId: number) => {
            if (!fieldIdMap.hasOwnProperty(fieldId)) {
                fieldIdsToEval.push(fieldId);
                fieldIdMap[fieldId] = true;
            }
        });

        if (updatedFieldIds) {
            updatedFieldIds.forEach((fieldId: number) => {
                if (!fieldIdMap.hasOwnProperty(fieldId)) {
                    fieldIdsToEval.push(fieldId);
                    fieldIdMap[fieldId] = true;
                }
            });
            if (!fieldIdMap.hasOwnProperty(DalFields.HistoryId)) {
                // we need to trigger the history change handlers (particularly for the History tab)
                fieldIdsToEval.push(DalFields.HistoryId);
                fieldIdMap[DalFields.HistoryId] = true;
            }
        } else {
            this.fields.forEach((field: IField) => {
                const fieldId = field.fieldDefinition.id;
                if (!fieldIdMap.hasOwnProperty(fieldId)) {
                    fieldIdsToEval.push(fieldId);
                    fieldIdMap[fieldId] = true;
                }
            });
        }

        // evaluateFields here actually takes the fieldIdsToEval, which is all fields we need to run
        // rules on, as well as set of alreadyChangedFields, which we know we need to notify the
        // control even if rules don't change them because the value themselves has changed
        // (there can be multiple controls for the same fieldID on the form)
        this.evaluateFields(fieldIdsToEval, alreadyChangedFields);
    }

    public evaluateFields(fieldIdsToEval: number[], alreadyChangedFields?: number[]) {
        const changedFields = new Set<number>();

        if (alreadyChangedFields) {
            alreadyChangedFields.forEach((fref: number) => {
                changedFields.add(fref);
            });
        }

        const changedFieldsByRules = this._getRuleEngine().evaluateFields(fieldIdsToEval);

        if (changedFieldsByRules) {
            changedFieldsByRules.forEach((fref: number) => {
                changedFields.add(fref);
            });
        }

        this._onWorkItemChanged([...changedFields.values()], { workItemChangeType: WorkItemChangeType.FieldEvaluate });
    }

    public resetManualFieldChanges() {
        this.manuallySetFields.clear();
    }

    public getFieldWatermark(): IObservableValue<number> {
        return this._fieldWatermark;
    }

    private updateFieldWatermark(action: string): void {
        this._fieldWatermark.notify(this._fieldWatermark.value + 1, action);
    }

    private _getRuleEngine(): RuleEngine {
        if (!this._ruleEngine) {
            const fieldRules: IFieldRule[] = [];

            this.fields.forEach((field: IField) => {
                if (field.fieldDefinition.rules) {
                    fieldRules.push({
                        fieldId: field.fieldDefinition.id,
                        rules: field.fieldDefinition.rules
                    });
                }
            });

            if (this.extensions) {
                this.extensions.forEach((extension: IWorkItemTypeExtension) => {
                    if (extension.fieldRules) {
                        for (const [fieldId, rules] of Object.entries(extension.fieldRules)) {
                            fieldRules.push({
                                fieldId: Number(fieldId),
                                rules: rules as any
                            });
                        }
                    }
                });
            }

            this._ruleEngine = new RuleEngine(this, fieldRules);
        }

        return this._ruleEngine;
    }

    private _splitTagNames(tagsText: string): string[] {
        if (tagsText) {
            return tagsText.trim().split(/;\s*/);
        } else {
            return [];
        }
    }

    public reset(): void {
        this.resetContributionErrorStatuses();
        this.resetChanges();
        this.relatedData = {};

        const changedFields: number[] = [DalFields.RelatedLinks, DalFields.BISURI, DalFields.LinkedFiles, DalFields.AttachedFiles, DalFields.Tags];
        if (this.fieldMap[CoreFieldRefNames.Parent]) {
            changedFields.push(CoreField.Parent);
        }

        this._onWorkItemChanged(changedFields, { workItemChangeType: WorkItemChangeType.Reset });

        this.updateFieldWatermark(WorkItemWatermarkAllAction);
        this._workItemFieldEventService.fireEvent({ workItem: this }, IWorkItemFieldEventType.WorkItemFieldsUpdated);
        this._workItemEventService.fireEvent({ workItem: this }, IWorkItemEventType.WorkItemReset);
    }

    public resetChanges(): void {
        let link: Link;

        // Resetting links by removing newly added links
        this._currentLinks = undefined;
        const allLinks = this._allLinks;

        for (let i = 0, l = allLinks.length; i < l; i++) {
            link = allLinks[i];
            // If link is new, removing it
            if (link.isNew()) {
                allLinks.splice(i--, 1);
                l--;
            } else {
                link.pendingDeletion = false;

                if (link.linkData.CommentChanged) {
                    link.linkData.Comment = link.comment;
                    link.linkData.CommentChanged = false;
                }

                if (link.linkData.LockChanged) {
                    link.linkData.Lock = link.isLocked;
                    link.linkData.LockChanged = false;
                }

                link.setState();
            }
        }

        this.cleanPromisesToWait();

        this.fieldUpdates.clear();

        this.evaluate();

        this.resetManualFieldChanges();

        this._resetWorkItemTypeChanges();

        this.evaluateDirty();
    }

    public async refresh(config?: {
        isDeleted?: boolean;
        doNotResetComment?: boolean;
        doNotResetIfManuallyDirty?: boolean;
        includeHistory?: boolean;
        doNotResetFields?: boolean;
    }): Promise<void> {
        if (this.isNew()) {
            this.reset();
        } else {
            const workItemDataManager = this._pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
            const workItemPayload = await workItemDataManager.beginGetWorkItemData(
                [this.id],
                config?.isDeleted,
                /*includeInRecentActivity*/ undefined,
                config?.includeHistory
            );
            if (!workItemPayload || workItemPayload.length === 0) {
                return Promise.reject(format(Resources.InvalidWorkItemId, this.id));
            }
            // Don't update work item through real-time SignalR functionality when someone else changed this work item if manually dirty fields exist, except discussion section
            if (config?.doNotResetIfManuallyDirty && this.isDirty(true, [CoreField.History])) {
                return;
            }
            this.updateWorkItemPayload(workItemPayload[0], config?.doNotResetComment, config?.doNotResetFields);
        }
    }

    public async refreshNewLinks(config?: { includeHistory?: boolean; keepDeleted?: boolean }): Promise<void> {
        const workItemDataManager = this._pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
        const workItemPayload = await workItemDataManager.beginGetWorkItemData([this.id], false, false, config?.includeHistory);
        if (!workItemPayload || workItemPayload.length === 0) {
            throw new Error(format(Resources.InvalidWorkItemId, this.id));
        }
        const data = workItemPayload[0];

        const updatedLinkInfos = [...(data.files ?? []), ...(data.relations ?? []), ...(data.relationRevisions ?? [])];

        const updatedLinks = updatedLinkInfos.map(li => Link.createFromLinkData(li));
        // addLinks skips duplicates, so this will just add new links
        this.addLinks(updatedLinks, config?.keepDeleted);
    }

    /**
     * Updates work item data from given payload
     * @param workItemPayload new work item payload

     * @param doNotResetComment If true, dont reset dirty comment after refresh
     */
    private async updateWorkItemPayload(workItemPayload: IWorkItemData, doNotResetComment?: boolean, doNotResetFields?: boolean): Promise<void> {
        const teamProject = workItemPayload.fields[CoreField.TeamProject];
        const workItemTypeName = workItemPayload.fields[CoreField.WorkItemType];

        // Evaluate if there was a change in the workitemtype and/or project
        // If yes, rebind the workitem form as per new fields
        // Otherwise, continue with loading the updatePayload into the workItem object
        await this._evaluateWorkItemTypeChange(teamProject, workItemTypeName);

        // Load the work item data and reset the fields.
        this.load(workItemPayload, !doNotResetFields, doNotResetComment);

        if (!doNotResetFields) {
            this.updateFieldWatermark(WorkItemWatermarkAllAction);
            this._workItemFieldEventService.fireEvent({ workItem: this }, IWorkItemFieldEventType.WorkItemFieldsUpdated);
        }

        this.evaluateDirty();
    }

    public async takeUpdateResult(
        context: IVssPageContext,
        updateResult: IWorkItemUpdateResult,
        update: IWorkItemUpdate,
        options?: { keepDeletedLinks?: boolean; ignoreTelemetry?: boolean; ignoreFieldUpdates?: boolean; fieldsToClear?: CoreField[] }
    ) {
        const { keepDeletedLinks, ignoreTelemetry } = options ?? {};
        const addSplitTiming = ignoreTelemetry
            ? () => {}
            : (timingName: string) => {
                  this._perfService.addSplitTiming(timingName);
              };
        addSplitTiming(`${WorkItemFormPerfConstants.TakeUpdateResult}.Start`);
        const updatedFieldIds: number[] = [];
        const prevId = this.id;
        let updatedRevision: undefined | number;
        try {
            this._loadTime = updateResult.loadTime;

            this._ruleEngine = undefined; // reset rule engine so that it should pick extension rules

            // We should perform field operations if revision or watermark changes.
            const hasNewRevision = updateResult.rev !== this.revision.value;
            const hasNewWatermark =
                updateResult.fields && this._fieldData && updateResult.fields[CoreField.Watermark] !== this._fieldData[CoreField.Watermark];

            if (hasNewRevision || hasNewWatermark) {
                this.setFieldValueById(CoreField.Id, updateResult.id, { preventFire: true });
                this.id = updateResult.id;
                if (updateResult.isCommentingAvailable !== undefined && updateResult.isCommentingAvailable !== null) {
                    this._isCommentingAvailable = updateResult.isCommentingAvailable;
                }

                // When the workitem ID is updated (i.e. when it is saved) make sure this is added to the cache
                if (prevId === 0) {
                    // TODO:Replat mimic fire event
                    // this.updateCachedIdOnSave();
                }

                this.setFieldValueById(CoreField.Rev, updateResult.rev, { preventFire: true });
                updatedRevision = updateResult.rev;

                let fieldId: string;
                const fieldsFromUpdateResult = updateResult.fields;

                // first we run over fields from update result and add those into our updatedFieldIDs
                // updatedFieldIds is our grouping of fields we know have changed from this save result, either by some other save operation that happened concurrently
                // or this save result which has some rules that run on the server like serverDefaultRules
                if (fieldsFromUpdateResult) {
                    for (fieldId in fieldsFromUpdateResult) {
                        if (fieldsFromUpdateResult.hasOwnProperty(fieldId)) {
                            this.setFieldValueById(+fieldId, fieldsFromUpdateResult[fieldId], { evaluate: false });
                            updatedFieldIds.push(+fieldId);
                        }
                    }
                }

                const fieldUpdates = this.fieldUpdates;
                const fieldData = this._fieldData;
                let newRevision: IFieldDataDictionary;

                // Checking to see whether a new work item saved
                if (updateResult.rev === 1) {
                    // In new work item case, there shouldn't be any revision around
                    fieldUpdates.forEach((update, fieldId) => {
                        // [liangzhu] WARNING: line below would cause inconsistency between locally saved revisions and server returned revisions.
                        //            if a field did not exist and only introduced in the save, it will be 'undefined' instead of 'null' (If get from MVC)
                        //            any consumer of revisions object must distinguish the sutble difference between 'undefined'(same as null) and '!hasOwnProperty' (look at revision+1)
                        //            I'm not touching the line just to be safe rather introduce regression, but if we do refactor this is candidate to consider.
                        fieldData[fieldId] = update.value;
                    });

                    if (fieldData[CoreField.History]) {
                        this._addDiscussionForRevision(fieldData[CoreField.History], fieldData);
                    }
                } else if (hasNewRevision) {
                    // Creating the new revision object
                    newRevision = {};

                    if (fieldData.hasOwnProperty(CoreField.ChangedBy)) {
                        newRevision[CoreField.ChangedBy] = fieldData[CoreField.ChangedBy];
                    }

                    fieldUpdates.forEach((update, fieldId) => {
                        // Updating the revision
                        newRevision[fieldId] = fieldData[fieldId];
                        // Updating the field data
                        fieldData[fieldId] = update.value;
                    });

                    /*
                    Relevant Bugs (mseng): 79911, 72720, 86139
                    We are creating a "newRevision" to keep track of historical changes, by storing the old data before the save.

                    For Example:
                    We were at Rev 4 before saving and are now at Rev 5 after saving.
                      -  "newRevision" will contain Rev 4 data
                      -  "fieldData" will contain Rev 5 data
                      -  "fieldUpdates" will contain Rev 6 data (currently empty because we just saved)

                    Note: This all makes sense, but the issue arises from the fact that the work item is considered dirty because field updates
                    will have an empty string or null set as the history later because the history text box is cleared. This has been fixed in the
                    comparison by ignoring empty string or null in the history field. See commit linked to work item 72720 on mseng.
                    */

                    if (fieldData.hasOwnProperty(CoreField.History)) {
                        if (!newRevision.hasOwnProperty(CoreField.History)) {
                            /*
                            This is the case where the user updates something but not the history
                            The message needs to be moved to the "newRevision" since it is not associated with this change
                            This fixes the issue of having the wrong changedby associated with the history (in discussion tab)

                            Note on how the history is displayed:
                            The first history will be generated from whats in field data. Then the rest is generated from the array of revisions
                            from newest to oldest.
                            */
                            newRevision[CoreField.History] = fieldData[CoreField.History];
                            delete fieldData[CoreField.History];
                        }
                    }

                    if (this._revisionsPopulated) {
                        this._revisions.push(newRevision);
                    }

                    if (fieldData[CoreField.History]) {
                        this._addDiscussionForRevision(fieldData[CoreField.History], fieldData);
                    }
                } else {
                    // For revisionless updates, we update the field data only.
                    fieldUpdates.forEach((update, fieldId) => {
                        if (fieldUpdates.hasOwnProperty(fieldId)) {
                            fieldData[fieldId] = update.value;
                        }
                    });
                }

                if (options?.fieldsToClear) {
                    options?.fieldsToClear.forEach(fieldId => this.fieldUpdates.delete(fieldId));
                }
                if (options?.ignoreFieldUpdates) {
                    fieldUpdates.forEach((update, fieldId) => {
                        if (updateResult.fields[fieldId]) {
                            this.fieldUpdates.delete(fieldId);
                        }
                    });
                }
                if (!options?.ignoreFieldUpdates && !options?.fieldsToClear) {
                    this.fieldUpdates.clear();
                }
            }
        } finally {
            addSplitTiming(`${WorkItemFormPerfConstants.TakeUpdateResult}.Complete`);
        }

        let linkUpdateResult: LinkUpdateResult;
        addSplitTiming(`${WorkItemFormPerfConstants.EvaluateLinkUpdates}.Start`);
        try {
            // then, we take a look to see if any links have changed from the update result and update (this code here just looks at links added/removed, it returns an array of fields related to links that have changed
            linkUpdateResult = await this._takeLinkUpdateResult(context, updateResult, update, keepDeletedLinks);

            // then we reset history (not sure why TODO: look into this)
            // TODO:Replat trigger reset history
            // this.resetHistory();

            // then we run the rules on the work item, handing it the two sets of fields we know have changed (this function evaluate here should really be consolidated into taking one paramater, but because this is experimental code right
            // now I am leaving as two separate ones. But we are just handing it the fields we know have changed
            this.evaluate(linkUpdateResult.changedFields, updatedFieldIds);
            this._onWorkItemChanged(updatedFieldIds, { workItemChangeType: WorkItemChangeType.FieldEvaluate });

            // finally this last step resets manual field changes (not sure what this does either)
            this.resetManualFieldChanges();
            this.evaluateDirty();
            this.updateInfoText();
        } finally {
            addSplitTiming(`${WorkItemFormPerfConstants.EvaluateLinkUpdates}.Complete`);
        }

        // Reset the original workitem type on saving successfully
        addSplitTiming(`${WorkItemFormPerfConstants.ResetAfterSave}.Start`);
        try {
            const typeChanged = this.hasWorkItemTypeChanged();
            const projectChanged = this.hasTeamProjectChanged();
            this._originalTypeData = this._typeData;
            this._originalProjectData = this._projectData;

            this._workItemEventService.fireEvent(
                {
                    workItem: this,
                    changedWorkItemLinks: linkUpdateResult.changedWorkItemLinks,
                    externalLinkChanged: linkUpdateResult.externalLinkChanged,
                    firstSave: prevId !== this.id,
                    typeChanged: typeChanged,
                    projectChanged: projectChanged
                },
                IWorkItemEventType.WorkItemSaved
            );

            // This may trigger re-render of some components, so do this at the end when we are about done
            if (updatedRevision !== undefined) {
                this.revision.value = updatedRevision;
            }
        } finally {
            addSplitTiming(`${WorkItemFormPerfConstants.ResetAfterSave}.Complete`);
        }
    }

    private _addDiscussionForRevision(historyText: string, revision: IFieldDataDictionary) {
        // Update the comment count in the field data on saving a comment successfully
        this._fieldData[CoreField.CommentCount] = this._getCommentCount();
        // Events_Services.getService().fire(Actions.DISCUSSION_ADDED, this, { historyText: historyText, revision: revision });
    }

    private async _takeLinkUpdateResult(
        context: IVssPageContext,
        updateResult: IWorkItemUpdateResult,
        update: IWorkItemUpdate,
        keepDeleted?: boolean
    ): Promise<LinkUpdateResult> {
        const changedFieldMap: { [fieldId: number]: boolean } = {};
        const changedWorkItemLinks: ILinkInfo[] = [];
        let externalLinkChanged = false;

        const fixLinkTargetAndType: (
            linkUpdateResult: ILinkUpdateResult
        ) => Promise<{ targetId: number; linkType: number; remoteHostId: string }> = async (linkUpdateResult: ILinkUpdateResult) => {
            if (linkUpdateResult.SourceID === this.id) {
                return {
                    targetId: linkUpdateResult.TargetID!,
                    linkType: linkUpdateResult.LinkType!,
                    remoteHostId: linkUpdateResult.RemoteHostId!
                };
            } else {
                return {
                    targetId: linkUpdateResult.SourceID!,
                    linkType: (await WorkItemLink.findLinkTypeEnd(context, linkUpdateResult.LinkType!)).oppositeEnd!.id,
                    remoteHostId: linkUpdateResult.RemoteHostId!
                };
            }
        };

        const findWorkItemLink = (targetId: number, linkTypeEndId: number, remoteHostId: string) => {
            let foundLink: WorkItemLink | undefined;

            this._allLinks.forEach((link: Link) => {
                if (link instanceof WorkItemLink && !link.isRemoved()) {
                    const wlink = link;
                    if (wlink.getTargetId() === targetId && (!link.remoteHostId || link.remoteHostId === remoteHostId)) {
                        if (wlink.getWitLinkTypeEndId() === linkTypeEndId) {
                            foundLink = wlink;
                            return false;
                        }
                    }
                }
            });

            return foundLink;
        };

        // set removed date for all non work itemlinks
        const deletedNonWorkItemLinks: { [filePath: string]: Link } = {};
        update.deletedLinks &&
            update.deletedLinks.forEach((link: Link) => {
                if (!(link instanceof WorkItemLink)) {
                    changedFieldMap[link.getFieldId()] = true;
                    link.linkData.RemovedDate = this.getFieldValueById(DalFields.LastChangedDateId) as Date;
                    if (link.linkData.FilePath) {
                        deletedNonWorkItemLinks[link.linkData.FilePath] = link;
                    }
                }
            });

        for (const linkUpdateResult of updateResult.deletedLinks) {
            // check if its a work item link
            if (!linkUpdateResult.ExtID) {
                const fixedLinkTargetAndType = await fixLinkTargetAndType(linkUpdateResult);
                const linkToDelete = findWorkItemLink(
                    fixedLinkTargetAndType.targetId,
                    fixedLinkTargetAndType.linkType,
                    fixedLinkTargetAndType.remoteHostId
                );

                if (linkToDelete) {
                    changedFieldMap[linkToDelete.getFieldId()] = true;
                    linkToDelete.linkData["Revised Date"] = linkUpdateResult.ChangedDate;
                    linkToDelete.linkData["Revised By"] = linkUpdateResult.ChangedBy;

                    // Keeping track of removed work item links
                    changedWorkItemLinks.push({
                        command: LinkCommand.Remove,
                        sourceId: this.id,
                        targetId: linkToDelete.getTargetId(),
                        linkData: linkToDelete.linkData
                    });
                }
            } else if (!externalLinkChanged && linkUpdateResult.FilePath) {
                const link = deletedNonWorkItemLinks[linkUpdateResult.FilePath];
                if (link instanceof ExternalLink) {
                    externalLinkChanged = true;
                }
            }
        }

        const addedNonWorkItemLinks: { [filepath: string]: Link } = {};

        update.addedLinks &&
            update.addedLinks.forEach((link: Link) => {
                if (!(link instanceof WorkItemLink) && link.linkData.FilePath) {
                    addedNonWorkItemLinks[link.linkData.FilePath] = link;
                }
            });

        for (const linkUpdateResult of updateResult.addedLinks) {
            let link: Link;
            if (linkUpdateResult.ExtID && linkUpdateResult.FilePath) {
                link = addedNonWorkItemLinks[linkUpdateResult.FilePath];
                if (link) {
                    changedFieldMap[link.getFieldId()] = true;

                    link.linkData.ExtID = linkUpdateResult.ExtID;
                    link.linkData.AddedDate = this.getFieldValueById(DalFields.LastChangedDateId) as Date;

                    if (link instanceof ExternalLink) {
                        externalLinkChanged = true;
                    }
                }
            } else {
                const fixedLinkTargetAndType = await fixLinkTargetAndType(linkUpdateResult);
                let linkToBeAdded = findWorkItemLink(
                    fixedLinkTargetAndType.targetId,
                    fixedLinkTargetAndType.linkType,
                    fixedLinkTargetAndType.remoteHostId
                );

                if (!linkToBeAdded) {
                    linkToBeAdded = await WorkItemLink.create(context, fixedLinkTargetAndType.linkType, fixedLinkTargetAndType.targetId);
                    this._allLinks.push(linkToBeAdded);
                }

                changedFieldMap[linkToBeAdded.getFieldId()] = true;
                linkToBeAdded.linkData["Changed Date"] = linkUpdateResult.ChangedDate;
                linkToBeAdded.linkData["Changed By"] = linkUpdateResult.ChangedBy;

                // Keeping track of added work item links
                changedWorkItemLinks.push({
                    command: LinkCommand.Add,
                    sourceId: this.id,
                    targetId: linkToBeAdded.getTargetId(),
                    linkData: linkToBeAdded.linkData
                });
            }
        }

        this.resetLinks(undefined, keepDeleted);

        const changedFieldIds: number[] = [];
        for (const fieldId in changedFieldMap) {
            if (changedFieldMap.hasOwnProperty(fieldId)) {
                changedFieldIds.push(+fieldId);
            }
        }

        return { changedFields: changedFieldIds, changedWorkItemLinks: changedWorkItemLinks, externalLinkChanged };
    }

    public load(data?: Partial<IWorkItemData>, resetFields?: boolean, doNotResetComment?: boolean, areaPathMru?: number, iterationPathMru?: number) {
        // should really be private
        let links: ILinkInfo[];

        this.dirty;
        this.manualDirty;

        data = data || ({} as IWorkItemData);

        this._isReadOnly = data.isReadOnly || false;

        this._loadTime = data.loadTime || new Date();

        this._fieldData = data.fields || {};

        // Trim leading/trailing whitespace for longTextFields
        for (const id in this._fieldData) {
            if (this._fieldData.hasOwnProperty(id)) {
                const def = this._fieldData[id].fieldDefinition;
                if (def) {
                    if (isLongTextField(def.type)) {
                        const fldData = this._fieldData[id];
                        if (fldData) {
                            this._fieldData[id] = fldData.trim();
                        }
                    } else if (def.isIdentity) {
                        // if it's an identity field, we want to ensure the value
                        // is in the WorkItemIdentityHelper cache so that any calls
                        // to retrieve it do not need a round trip to the graph API.
                        const fldData = this._fieldData[id];
                        if (fldData) {
                            setResolvedByDescriptorIfWorkItemIdentityRef(fldData);
                        }
                    }
                }
            }
        }

        this._revisions = data.revisions || [];

        if (!resetFields && this.fieldUpdates) {
            //do not clear field updates if reset fields is not required
            this.dirty.value = this.fieldUpdates.size > 0;
        } else if (doNotResetComment && this.fieldUpdates.has(CoreField.History)) {
            this.fieldUpdates = new Map<number, IFieldUpdate>([[CoreField.History, this.fieldUpdates.get(CoreField.History)!]]);
            this.dirty.value = true;
        } else {
            this.fieldUpdates = new Map<number, IFieldUpdate>();
        }

        Object.keys(this._fieldData).forEach((fieldId: string) => {
            this._defineFieldById(+fieldId);
        });

        if (resetFields) {
            this.fields.forEach((field: IField) => {
                resetField(field);
            });

            this.resetContributionErrorStatuses();
        }

        // Populating all the links here (including attachments, external links,
        // related links and work item links. Deleted links also exist
        //      files:              attachments, external links and hyperlinks (deleted files are included as well)
        //      relations:          work item links
        //      relationRevisions:  deleted work item links

        links = [];
        if (data.files) {
            links.push(...data.files);
        }

        if (data.relations) {
            links.push(...data.relations);
        }

        if (data.relationRevisions) {
            links.push(...data.relationRevisions);
        }

        this._allLinks = [];
        this._currentLinks = undefined;
        this._allLinks.push(...links.map(link => Link.createFromLinkData(link)));
        this.relatedData = {};

        this.referencedPersons = data.referencedPersons || {};

        this.id = this.getFieldValueById(CoreField.Id) || 0;

        if (this.id === 0) {
            if (data.tempId) {
                this.tempId = data.tempId;
                WorkItem.reserveTempId(this.tempId);
            } else {
                this.tempId = WorkItem.getTempId();
            }
        }

        this.revision.value = this.getFieldValueById(CoreField.Rev) || 0;

        this._isCommentingAvailable = data.isCommentingAvailable ?? false;

        if (this.isNew()) {
            // If no data is provided, we are assuming this work item to be new
            const projectId = this._projectData.id;
            this.setFieldValueById(CoreField.AreaId, areaPathMru ?? projectId, { setByRule: true });
            this.setFieldValueById(CoreField.IterationId, iterationPathMru ?? projectId, { setByRule: true });
            this.setFieldValueById(CoreField.WorkItemType, this._typeData.name, { setByRule: true });
            this.setFieldValueById(CoreField.Id, 0, { setByRule: true }); //setting it explicitly for avoiding undefined when you apply a template for a new wi

            // By default 'new' work items will have an empty revision collection which will be updated on 'Save'
            this._revisionsPopulated = true;
        } else {
            // If there are no revisions in the retrieved data then this work item was requested without revisions.
            this._revisionsPopulated =
                (data.revisions != undefined && data.revisions != null && data.revisions.length > 0) ||
                (data.relationRevisions != undefined && data.relationRevisions != null && data.relationRevisions.length > 0);
        }

        // TODO:Replat fire these events
        // this.resetHistory();
        // this._resetDiscussion();

        const changedFields: number[] = [DalFields.RelatedLinks, DalFields.AttachedFiles, DalFields.BISURI, DalFields.LinkedFiles];

        if (this.fieldMap[CoreFieldRefNames.Parent]) {
            changedFields.push(CoreField.Parent);
        }
        this.evaluate(changedFields);

        if (this.isNew()) {
            // This is used to identify which fields are explicitly changed.
            // For a newly created work item, there is nothing in fieldData.
            // This causes every change to be added to fieldUpdates even though
            // field value is not changed actually.
            this.initialValues = new Map<number, IFieldUpdate>(this.fieldUpdates);
            this.dirty.value = true;
        }

        if (!resetFields && this.manuallySetFields.size > 0) {
            this.dirty.value = true;
            this.manualDirty.value = true;
        } else if (doNotResetComment && this.manuallySetFields.has(CoreField.History)) {
            this.manuallySetFields = new Map<number, any>([[CoreField.History, this.manuallySetFields.get(CoreField.History)]]);
            this.dirty.value = true;
            this.manualDirty.value = true;
        } else {
            this.manuallySetFields = new Map<number, any>();
        }
    }

    public resetContributionErrorStatuses(): void {
        if (this._contributionErrorMap && Object.keys(this._contributionErrorMap).length > 0) {
            this._contributionErrorMap = {};
            // this.fireWorkItemErrorChanged();
        }
    }

    public restoreLinkUpdates(changedLinks: ILinkUpdates) {
        // Restore added and removed links.
        if (changedLinks) {
            if (changedLinks.addedLinks) {
                const addedLinks = changedLinks.addedLinks;

                for (const linkData of addedLinks) {
                    const newLink = Link.createFromLinkData(linkData);
                    this.addLinks([newLink]);
                }
            }

            if (changedLinks.deletedLinks) {
                const deletedLinks = changedLinks.deletedLinks;
                for (const linkData of deletedLinks) {
                    const newLink = this.findLink(Link.createFromLinkData(linkData));

                    // If a matching link was found, delete it.
                    if (newLink) {
                        this.removeLinks([newLink]);
                    }
                }
            }
        }
    }

    public addLinks(links: Link[], keepDeleted?: boolean) {
        if (this.isReadOnly()) {
            return;
        }

        const fieldIdMap: { [id: number]: number } = {};

        for (const currLink of links) {
            // see if a link like the new one already exists on the work item
            let linkAlreadyExistsAsDeleted = false;
            let isDuplicate = false;
            for (const link of this._allLinks) {
                if (!link.isRemoved() && currLink.isDuplicate(link)) {
                    if (link.pendingDeletion) {
                        // If there is a duplicate link which was removed without saving the workitem,
                        // then instead of adding a new link, we just mark the existing one as not deleted
                        if (!localeComparer(link.getComment() ?? "", currLink.getComment() ?? "")) {
                            link.setComment(currLink.getComment());
                        }

                        linkAlreadyExistsAsDeleted = true;
                        link.pendingDeletion = false;
                        break;
                    } else {
                        isDuplicate = true;
                        if (this._historyLinksOptimizationsEnabled) {
                            // _allLinks cannot contain a duplicate of the current link twice, so we can break here
                            break;
                        }
                    }
                }
            }

            // Skip adding duplicate links
            if (isDuplicate) {
                continue;
            }

            const fieldId = currLink.getFieldId();
            if (!linkAlreadyExistsAsDeleted) {
                fieldIdMap[fieldId] = (fieldIdMap[fieldId] || 0) + 1;
                this._allLinks.push(currLink);
            } else {
                fieldIdMap[fieldId] = 0;
            }
        }

        if (links.length) {
            this.resetLinks(/*removeDeletedNewlinks*/ undefined, /*skipRemovedCheck*/ keepDeleted);

            const fieldIds: number[] = [];

            let linkCountFieldId: number;
            for (const fieldIdStr in fieldIdMap) {
                if (fieldIdMap.hasOwnProperty(fieldIdStr)) {
                    const fieldId = +fieldIdStr;
                    fieldIds.push(fieldId);
                    linkCountFieldId = Link.getCountFieldId(fieldId);
                    this.evaluateField(linkCountFieldId, true, this.getFieldValueById(linkCountFieldId) - fieldIdMap[fieldId]);
                }
            }

            this.evaluateDirty();
            this.evaluateFields(fieldIds, fieldIds);
            this.evaluateDirty();
        }
    }

    public removeLinks(links: Link[], keepDeleted?: boolean) {
        if (this.isReadOnly()) {
            return;
        }

        let removedCount = 0;
        const fieldIdMap: { [id: number]: number } = {};

        let fieldId: number;
        for (const link of links) {
            // Skipping locked links
            if (!link.getIsLocked()) {
                fieldId = link.getFieldId();
                link.remove({ preventEvent: true });
                fieldIdMap[link.getFieldId()] = (fieldIdMap[fieldId] || 0) + 1;
                removedCount++;
            }
        }

        if (removedCount) {
            this.resetLinks(/*removeDeletedNewlinks*/ true, /*skipRemovedCheck*/ keepDeleted);

            const fieldIds: number[] = [];

            let linkCountFieldId: number;
            for (const fieldIdStr in fieldIdMap) {
                if (fieldIdMap.hasOwnProperty(fieldIdStr)) {
                    const fieldId = +fieldIdStr;
                    fieldIds.push(+fieldId);
                    linkCountFieldId = Link.getCountFieldId(fieldId);
                    this.evaluateField(linkCountFieldId, true, this.getFieldValueById(linkCountFieldId) + fieldIdMap[fieldId]);
                }
            }

            this.evaluateDirty();
            this.evaluateFields(fieldIds, fieldIds);
            this.evaluateDirty();
        }
    }

    public findLink(link: Link): Link | undefined {
        const allLinks = this._allLinks;

        for (const currLink of allLinks) {
            if (currLink.equals(link)) {
                return currLink;
            }
        }

        return undefined;
    }

    public getLinkUpdates(update?: IWorkItemUpdate): ILinkUpdates {
        const deletedLinks: ILinkInfo[] = [];
        const updatedLinks: ILinkInfo[] = [];
        const addedLinks: ILinkInfo[] = [];

        for (const link of this._allLinks) {
            if (link.isRemoved()) {
                continue;
            }

            if (link.pendingDeletion) {
                deletedLinks.push(link.linkData);
                if (update && update.deletedLinks) {
                    update.deletedLinks.push(link);
                }
            } else if (link.isNew()) {
                addedLinks.push(link.linkData);
                if (update && update.addedLinks) {
                    update.addedLinks.push(link);
                }
            } else if (link.pendingUpdate) {
                updatedLinks.push(link.linkData);
                if (update && update.updatedLinks) {
                    update.updatedLinks.push(link);
                }
            }
        }

        const links: ILinkUpdates = {};
        if (deletedLinks.length > 0) {
            links.deletedLinks = deletedLinks;
        }

        if (updatedLinks.length > 0) {
            links.updatedLinks = updatedLinks;
        }

        if (addedLinks.length > 0) {
            links.addedLinks = addedLinks;
        }

        return links;
    }

    public resetLinks(removeDeletedNewlinks?: boolean, skipRemovedCheck?: boolean) {
        let link: Link;
        this._currentLinks = undefined;

        this._allLinks = this._allLinks.filter(link => {
            if (removeDeletedNewlinks && link.pendingDeletion && link.isNew()) {
                return false;
            }

            if (skipRemovedCheck && link.getLinkType() != WorkItemLinkConstants.ATTACHEDLINKTYPE) {
                return true;
            }

            // Always delete links that are removed on the server
            if (link.isRemoved()) {
                return false;
            }

            return true;
        });

        if (removeDeletedNewlinks) {
            const allLinks = this._allLinks;

            for (let i = 0, l = allLinks.length; i < l; i++) {
                link = allLinks[i];

                // if a new link is deleted then delete it from our list too
                if (link.pendingDeletion && link.isNew()) {
                    allLinks.splice(i--, 1);
                    l--;
                }
            }
        }

        for (const link of this._allLinks) {
            link.setState(true); // Reset dirty flags
        }
    }

    public getLinks(includeDeleted?: boolean): Link[] {
        if (includeDeleted) {
            return this._allLinks;
        }
        if (!this._currentLinks) {
            this._currentLinks = this._allLinks.filter(link => !link.pendingDeletion && !link.isRemoved());
        }

        return this._currentLinks;
    }

    private _getLinkCount(type: any, original: boolean): number {
        let count = 0;
        const links = this.getLinks();

        links.forEach((link: Link) => {
            if (original && link.isNew()) {
                return;
            }

            if (type) {
                if (link instanceof type) {
                    count++;
                }
            } else {
                count++;
            }
        });

        return count;
    }

    private _getParentLinkId(original: boolean): number | undefined {
        const links = this.getLinks();

        for (const link of links) {
            if (original && link.isNew()) {
                continue;
            }

            if (link instanceof WorkItemLink) {
                const workItemLink: WorkItemLink = link;

                if (workItemLink.getWitLinkTypeEndId() === CoreLinkTypes.Parent) {
                    return workItemLink.getTargetId();
                }
            }
        }

        return undefined;
    }

    public computeFieldValue(fieldId: number, baseFieldId: number, revision?: number): { value: any; success: boolean } {
        let baseFieldValue: any /* string or number */;
        const result: { value: any; success: boolean } = { success: false } as any;

        if (revision !== undefined && revision >= 0) {
            // this code path will be used if valid revision was passed
            baseFieldValue = this.getFieldValueByRevision(baseFieldId, revision);
        } else {
            // this code path will be used if no revision was used
            baseFieldValue = this.getFieldValueById(baseFieldId);
        }

        const calcNodeLevel = (level: number) => {
            if (baseFieldValue) {
                const node = this.project.getNodeById(baseFieldValue as number);

                if (node) {
                    let segments: string[];
                    if ((node as IReferencedNode).path) {
                        segments = getReferencedNodeSegmenets((node as IReferencedNode).path);
                    } else {
                        segments = getNodeSegments(node as INode);
                    }
                    result.value = segments[level];
                    result.success = true;
                }
            }
        };

        switch (fieldId) {
            case CoreField.AreaId:
            case CoreField.IterationId:
                if (baseFieldValue) {
                    const node = this.project.getNode(
                        baseFieldValue as string,
                        fieldId === CoreField.AreaId ? TreeNodeStructureType.Area : TreeNodeStructureType.Iteration
                    );

                    if (node) {
                        result.value = node.id;
                        result.success = true;
                    }
                }
                break;
            case DalFields.AreaLevel1:
            case DalFields.IterationLevel1:
                calcNodeLevel(0);
                break;
            case DalFields.AreaLevel2:
            case DalFields.IterationLevel2:
                calcNodeLevel(2);
                break;
            case DalFields.AreaLevel3:
            case DalFields.IterationLevel3:
                calcNodeLevel(3);
                break;
            case DalFields.AreaLevel4:
            case DalFields.IterationLevel4:
                calcNodeLevel(4);
                break;
            case DalFields.AreaLevel5:
            case DalFields.IterationLevel5:
                calcNodeLevel(5);
                break;
            case DalFields.AreaLevel6:
            case DalFields.IterationLevel6:
                calcNodeLevel(6);
                break;
            case DalFields.AreaLevel7:
            case DalFields.IterationLevel7:
                calcNodeLevel(7);
                break;
            case CoreField.AreaPath:
            case CoreField.IterationPath:
                if (baseFieldValue) {
                    const node = this.project.getNodeById(baseFieldValue as number);

                    /* TODO:Replat -- support project move
                    if (!node) {
                        // In case the workitem was moved on the client and not yet saved (move in progress),
                        // we should fall back to original project, from which it was moved to the current project,
                        // to find the given node to display in history
                        const originalProjectName: string = this.getFieldValueByRevision(CoreField.TeamProject, revision!);
                        if (originalProjectName) {
                            try {
                                const originalProject = this.store.getProject(originalProjectName);
                                if (originalProject.nodesCacheManager.isNodesCacheAvailable()) {
                                    // Nodes call should be already initiated by the beginGetWorkItemTypes call
                                    // However, if it has not yet returned, ignore and proceed with the fallback logic
                                    node = originalProject.nodesCacheManager.findNodeById(baseFieldValue);
                                }
                            } catch (error) {
                                // Fall back to the original logic of displaying "removed node"
                            }
                        }
                    }*/

                    if (node) {
                        result.value = (node as IReferencedNode).path || getPath(node as INode, 1);
                        result.success = true;
                    } else {
                        // pioneer bug (1228743): deleted node, show it as (deleted area/iteration path) instead of falling back to the latest value (wrong)
                        // Future: we should send the delete data down to the client we can display the original deleted path.
                        result.value = fieldId === CoreField.AreaPath ? Resources.DeletedAreaPath : Resources.DeletedIterationPath;
                        result.success = true;
                    }
                }
                break;
            case CoreField.NodeName:
                if (baseFieldValue) {
                    const node = this.project.getNodeById(baseFieldValue as number);

                    if (node) {
                        result.value = node.name;
                        result.success = true;
                    }
                }
                break;
        }

        return result;
    }

    public getFieldValueByRevision(fieldId: number, revision: number): any {
        const revs = this._revisions;
        const fieldIdStr = fieldId + "";

        if (revision < 0) {
            // TODO: We need to check special revisions here
            return null;
        } else {
            // Trying to get the computed fields first
            if (fieldId === CoreField.History) {
                // Finding the proper revision by the rev number
                if (revision < revs.length) {
                    return revs[revision][fieldId];
                } else {
                    return this._fieldData[fieldId];
                }
            } else {
                const computed = this.getComputedFieldValue(fieldId, revision);
                if (computed && computed.success) {
                    return computed.value;
                }

                let result;
                let found = false;
                for (let i = revision; i < revs.length; i++) {
                    if (revs[i].hasOwnProperty(fieldIdStr)) {
                        result = convertPotentialIdentityRefFromFieldValue(this._pageContext, revs[i][fieldIdStr]);
                        found = true;
                        break;
                    }
                }
                // *IMPORTANT* Why we need a found flag and not compare result===undefined? Because the server/local behavior diverges and need handle both.
                // Field "Resolved By" - when user "Liang" resolves a work item and save as latest rev 2
                // Rev       | MVC    | Client      | Note
                // 1         | <null> | <undefined> | This revision is pushed after save happens in WorkItem.TakeUpdateResult
                // ---------------------------------------------------------------------------
                // Latest(2) | Liang  | Liang       |
                // Logically we should set these as null in the WorkItem.TakeUpdateResult code, but to keep code change minimal we handle it here.

                if (!found) {
                    // fallback to latest value, no changes occurred between [revision,latest]
                    result = this.getFieldValueById(fieldId, true);
                }

                return result;
            }
        }
    }

    public getComputedFieldValue(fieldId: number, revisionIndex?: number): { success: boolean; value: any } | null {
        const fieldIdStr = fieldId + "";

        // Use team project, iteration path, area path in the specified revision if not null.
        // Server sends these 3 values for each revision even if value didn't change to support work item move scenario
        // However it's possible to get a null value if the resolution failed on server side - for instance if area path was removed.
        if (
            revisionIndex !== undefined &&
            revisionIndex >= 0 &&
            (fieldId === CoreField.TeamProject || fieldId === CoreField.IterationPath || fieldId === CoreField.AreaPath)
        ) {
            const revision = this._revisions[revisionIndex];
            if (revision && revision.hasOwnProperty(fieldIdStr)) {
                const fieldValue = revision[fieldIdStr];
                if (fieldValue) {
                    return { success: true, value: fieldValue };
                }
            }
        }

        switch (fieldId) {
            case CoreField.TeamProject:
                return this.getFieldValueById(fieldId, true);

            case CoreField.NodeName:
            case CoreField.AreaPath:
            case DalFields.AreaLevel1:
            case DalFields.AreaLevel2:
            case DalFields.AreaLevel3:
            case DalFields.AreaLevel4:
            case DalFields.AreaLevel5:
            case DalFields.AreaLevel6:
            case DalFields.AreaLevel7:
                return this.computeFieldValue(fieldId, CoreField.AreaId, revisionIndex);

            case CoreField.IterationPath:
            case DalFields.IterationLevel1:
            case DalFields.IterationLevel2:
            case DalFields.IterationLevel3:
            case DalFields.IterationLevel4:
            case DalFields.IterationLevel5:
            case DalFields.IterationLevel6:
            case DalFields.IterationLevel7:
                return this.computeFieldValue(fieldId, CoreField.IterationId, revisionIndex);

            default:
                return null;
        }
    }

    public isChangeProjectInProgress(): boolean {
        return this._isChangeProjectInProgress;
    }

    /**
     * Copy value of given field from this work item to the one specified in workItem.
     */
    private _copyField(workItem: IWorkItem, fd: IFieldDefinition, type: WorkItemType) {
        // Checking to see whether the field is cloneable or not
        // Skipping also the title if the source work item is used as a template
        if (fd && isCloneable(fd)) {
            // Trying to get the field within the current work item type
            const f = this.getFieldById(fd.id);

            // If the field exists...
            if (f) {
                // getting its value and checking it's set or not
                const val = this.getFieldValueById(fd.id);
                if (!isEmpty(val)) {
                    // Ignoring paths out of the target project
                    if (fd.id === CoreField.AreaId || fd.id === CoreField.IterationId) {
                        if (typeof val !== "number" || this.project.id !== type.project.id) {
                            return;
                        }
                    }

                    // Setting the field value of the newly created work item
                    workItem.setFieldValueById(fd.id, val);
                }
            }
        }
    }

    public async copy(
        context: IVssPageContext,
        projectId: string,
        typeData: IWorkItemTypeData,
        copyLinks: boolean = true,
        copyAttachments: boolean = true,
        copyHistory: boolean = true,
        excludedFields: number[] = [],
        createLinkToSource: boolean = true,
        excludedLinkTypes?: number[]
    ): Promise<IWorkItem> {
        const newAttachments: Attachment[] = [];
        const newLinks: Link[] = [];
        const excludedHash: { [fieldId: number]: boolean } = {};
        const telemetryService = context.getService<IVssTelemetryService>("IVssTelemetryService");

        const modelService = this._pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        const nodeService = this._pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");

        const [fieldLookup, areaNodes, iterationNodes] = await Promise.all([
            modelService.getFieldLookup(projectId),
            nodeService.getAreaPathNodes(projectId),
            nodeService.getIterationNodes(projectId)
        ]);

        if (!fieldLookup || !areaNodes || !iterationNodes) {
            throw Error(); // TODO: message
        }

        let project: Project;
        if (projectId === this.project.guid) {
            project = this.project;
        } else {
            const dataManager = this._pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
            const fieldProjectData = await dataManager.beginGetFieldProjectData(projectId);
            if (!fieldProjectData) {
                throw Error();
            }
            const projectData = {
                ...fieldProjectData.projects[0],
                areaNodes,
                iterationNodes
            };
            project = new Project(projectData);
        }
        const type = new WorkItemType(this._pageContext, typeData, project, fieldLookup);

        // Ensuring parameters
        createLinkToSource = typeof createLinkToSource !== "boolean" ? true : createLinkToSource;

        telemetryService.publishEvent("WIT", "WorkItem.IncludeWorkItemLinksAndAttachments", {
            action: "Telemetry for Copy links and attachments checkbox",
            copyLinks: copyLinks,
            copyAttachments: copyAttachments
        });

        // Set of fields to always exclude during copy since they don't make sense to copy over
        const fieldRefNamesToExclude = [
            OobFieldRefNames.ResolvedBy,
            OobFieldRefNames.ResolvedDate,
            OobFieldRefNames.ClosedBy,
            OobFieldRefNames.ClosedDate,
            "Microsoft.Sync.ProjSrv.RequestedAssnGuid",
            "Microsoft.Sync.ProjSrv.RequestedProjGuid",
            "Microsoft.Sync.ProjSrv.ProjGuid",
            "Microsoft.Sync.ProjSrv.TaskGuid"
        ];

        excludedFields = excludedFields || [];
        for (const refName of fieldRefNamesToExclude) {
            const field = this.getFieldByName(refName);
            if (field) {
                excludedFields.push(field.fieldDefinition.id);
            }
        }

        // Populating excluded hash to make check easier
        for (const excludedField of excludedFields) {
            excludedHash[excludedField] = true;
        }

        // Creating the work item
        const workItem = await modelService.createWorkItem(typeData, projectId);

        // Events_Services.getService().fire(Actions.WORKITEM_COPIED, workItem);

        // Copying fields from trigger list
        for (const fieldId of type.triggerList) {
            if (!excludedHash[fieldId]) {
                // Getting field definition
                const fd = this.getFieldById(fieldId)?.fieldDefinition;
                if (fd) {
                    this._copyField(workItem, fd, type);
                }
                excludedHash[fieldId] = true;
            }
        }

        // Copying the rest of the fields
        for (const fieldEntry of type.fields) {
            const fieldDef = this.getFieldById(fieldEntry.id)?.fieldDefinition;
            if (fieldDef && !excludedHash[fieldDef.id]) {
                this._copyField(workItem, fieldDef, type);
            }
        }

        if (copyHistory) {
            let historyText: string;
            if (!this.isNew()) {
                const linkStr = this._isMarkdownDiscussionEnabled
                    ? `#${this.id}`
                    : `<a href='x-mvwit:workitem/${this.id}'>${this._typeData.name} ${this.id}</a>`;
                let copiedWithStr = Resources.CreateCopyCopiedFrom;
                if (copyLinks && copyAttachments) {
                    copiedWithStr = Resources.CreateCopyCopiedWithAllLinksAndAttachmentsFrom;
                } else if (copyLinks) {
                    copiedWithStr = Resources.CreateCopyCopiedWithAllLinksFrom;
                } else if (copyAttachments) {
                    copiedWithStr = Resources.CreateCopyCopiedWithAllAttachmentsFrom;
                }
                historyText = format(copiedWithStr, linkStr);
            } else {
                historyText = Resources.CreateCopyCopiedFromNewWorkItem;
            }

            // Adding history text
            workItem.setFieldValueById(CoreField.History, historyText);
        }

        if (copyLinks || copyAttachments) {
            // Copying links
            const links = this.getLinks();
            for (const link of links) {
                // Creating the clone of the link
                const clone = await link.clone(context, workItem);

                if (clone instanceof Attachment) {
                    if (copyAttachments) {
                        newAttachments.push(clone);
                    }
                } else if (copyLinks && clone && (!excludedLinkTypes || excludedLinkTypes.indexOf(link.linkData.LinkType ?? Number.NaN) === -1)) {
                    newLinks.push(clone);
                }
            }
        }

        // Creating a link to the original if the original is a saved item
        if (!this.isNew() && createLinkToSource) {
            // Spec currently calls for a related link. However, related links are directionless,
            // so its difficult to makeout without looking at created date, which item was the copied
            // item, and which item was the source
            newLinks.push(
                await WorkItemLink.create(context, await WorkItemLink.findLinkTypeEnd(context, LinkTypeName.Related_Link_Name), this.id, "")
            );
        }

        if (newAttachments.length > 0) {
            newLinks.push(...newAttachments);
        }

        if (newLinks.length > 0) {
            workItem.addLinks(newLinks);
        }

        const changedFields: number[] = [DalFields.RelatedLinks, DalFields.AttachedFiles, DalFields.BISURI, DalFields.LinkedFiles];
        if (this.fieldMap[CoreFieldRefNames.Parent]) {
            changedFields.push(CoreField.Parent);
        }
        workItem.evaluate(changedFields);

        return workItem;
    }

    public async copyWithChildren(
        context: IVssPageContext,
        projectId: string,
        typeData: IWorkItemTypeData,
        copyLinks?: boolean,
        copyAttachments?: boolean,
        copyHistory?: boolean,
        excludedFields?: number[],
        createLinkToSource?: boolean
    ): Promise<IWorkItem> {
        const copiedItem = await this.copy(
            context,
            projectId,
            typeData,
            copyLinks,
            copyAttachments,
            copyHistory,
            excludedFields,
            createLinkToSource,
            [CoreLinkTypes.Child]
        );

        if (!copiedItem.isValid()) {
            const error = copiedItem.getInvalidFields()[0].error;
            throw Error(error);
        }

        const childIds = this.getLinks()
            .filter(l => l.linkData.LinkType === CoreLinkTypes.Child)
            .map(l => l.linkData.ID!);
        const maxChildrenToCopy = 100;
        if (childIds.length > maxChildrenToCopy) {
            throw Error(format(Resources.CreateCopyTooManyChildren, maxChildrenToCopy));
        }
        if (childIds.length > 0) {
            const modelService = this._pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
            const children = await modelService.getWorkItems(childIds);
            const filteredChildren = children.filter(c => c.project.guid === copiedItem.project.guid);
            const copiedChildren = await mapAsyncSerial(filteredChildren, async c => {
                const copiedChild = await c.copy(
                    context,
                    projectId,
                    c.getTypeData(),
                    copyLinks,
                    copyAttachments,
                    copyHistory,
                    excludedFields,
                    createLinkToSource,
                    [CoreLinkTypes.Parent]
                );
                if (!copiedChild.isValid()) {
                    const error = copiedChild.getInvalidFields()[0].error;
                    throw Error(format(Resources.CreateCopyErrorCopyingChild, c.id, error));
                }
                return copiedChild;
            });

            // we have to save the children so that they have ids so we can create links to them
            const savingService = context.getService<IWorkItemSavingService>("IWorkItemSavingService");

            await savingService.saveWorkItemsBatchAsync(copiedChildren);

            const newLinks = await mapAsyncSerial(copiedChildren, c => WorkItemLink.create(context, LinkTypeName.Child_Link_Name, c.id));
            copiedItem.addLinks(newLinks);
        }

        const telemetryService = context.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("WIT", "WorkItem.CopyWithChildren", {
            action: "Copy work item with children",
            copyLinks,
            copyAttachments,
            children: childIds.length
        });

        return copiedItem;
    }

    public changeWorkItemType(
        workItemTypeData: IWorkItemTypeData,
        projectData?: IProjectDataWithNodes,
        extensions?: IWorkItemTypeExtension[],
        destinationProjectFields?: IFieldEntry[]
    ) {
        this._isChangeProjectInProgress = !!projectData && this.project.id !== projectData.id;
        const prevTypeId = this._typeData.referenceName;

        const fieldNameToValueMap = this._getFieldChangeOnBugTypeChanged(this._typeData, workItemTypeData);

        this._typeData = workItemTypeData;
        this._ruleEngine = undefined;
        this._initializeFields(workItemTypeData);

        // Now that we've initialized the fields for the *new* work item type, we also need to define any field for which we still have
        // data (i.e., fields that only existed on the *old* work item type).
        for (const fieldRef of Object.keys(this._fieldData)) {
            this._defineFieldById(Number(fieldRef));
        }

        destinationProjectFields?.forEach(field => {
            if (!this.fieldMapById[field.id]) {
                this._defineField(field);
            }
        });

        // reinitialize extensions
        this.extensions = extensions || [];
        this._defineExtensionFields();

        if (projectData && this._isChangeProjectInProgress) {
            this._setProject(projectData);
        }

        if (prevTypeId !== workItemTypeData.referenceName) {
            this.setFieldValueById(CoreField.WorkItemType, workItemTypeData.name);

            for (const fieldName in fieldNameToValueMap) {
                this.setFieldValueByName(fieldName, fieldNameToValueMap[fieldName]);
            }
        }

        this.evaluate();
        this._isChangeProjectInProgress = false;
    }

    private _setProject(projectData: IProjectDataWithNodes) {
        this._projectData = projectData;
        this.project = new Project(projectData);
        this.setFieldValueById(CoreField.TeamProject, projectData.name, { preventFire: true });
    }

    private _getFieldChangeOnBugTypeChanged(prevWorkItemType: IWorkItemTypeData, workItemType: IWorkItemTypeData): { [field: string]: any } {
        // When change wit type from or to bug, we do best effort to populate ReproSteps field to Description field and vice versa.
        const fieldNameToValueMap: { [field: string]: any } = {};

        const prevTypeBug = this._isBugTypeChange(prevWorkItemType);
        const newTypeBug = this._isBugTypeChange(workItemType);

        if (!prevTypeBug && !newTypeBug) {
            return fieldNameToValueMap;
        }

        const message = `<b><u><br/><br/>${format(
            Resources.WorkItemChangedReproDescriptionMessage,
            this.getFieldByName(prevTypeBug ? BugWITFieldReferenceNames.ReproSteps : CoreFieldRefNames.Description)?.fieldDefinition.name,
            prevWorkItemType.name,
            workItemType.name
        )}</u></b><br/>`;

        const reproSteps = this.getFieldValueByName(BugWITFieldReferenceNames.ReproSteps);
        const description = this.getFieldValueByName(CoreFieldRefNames.Description);

        if (prevTypeBug) {
            // change work item type from bug category.
            if (reproSteps && !isHtmlVisuallyBlank(reproSteps)) {
                fieldNameToValueMap[CoreFieldRefNames.Description] = description ? description + message + reproSteps : message + reproSteps;
            }
        } else if (newTypeBug) {
            // change work item type to bug cateogry.
            if (description && !isHtmlVisuallyBlank(reproSteps)) {
                fieldNameToValueMap[BugWITFieldReferenceNames.ReproSteps] = reproSteps ? reproSteps + message + description : message + description;
            }
        }
        return fieldNameToValueMap;
    }

    private _isBugTypeChange(workItemType: IWorkItemTypeData): boolean {
        if (workItemType.fields) {
            // assume that the given type is a Bug type if it has 'repro steps' field. This is a best effort so if type was customized
            // w/o OOB repro steps, then we assume it's not a bug type and skip field copy logic between repro steps and description fields
            return workItemType.fields.some(id => {
                return equals(this._fieldLookup[id]?.referenceName ?? "", BugWITFieldReferenceNames.ReproSteps, true);
            });
        }

        return false;
    }

    private async _evaluateWorkItemTypeChange(teamProject: string, workItemTypeName: string) {
        if (equals(teamProject, this._projectData.name, true) && equals(workItemTypeName, this._originalTypeData.name, true)) {
            this._resetWorkItemTypeChanges();
        }
        if (!equals(workItemTypeName, this._typeData.name, true)) {
            let projectDataWithNodes = this._projectData;
            const dataManager = this._pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
            if (!equals(teamProject, this._projectData.name, true)) {
                const nodeService = this._pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");
                const projects = await dataManager.beginGetFieldProjectData(teamProject);
                const areaNodes = await nodeService.getAreaPathNodes(teamProject);
                const iterationNodes = await nodeService.getIterationNodes(teamProject);
                const projectData = projects?.projects.find(p => p.name === teamProject);

                if (!projectData || !areaNodes || !iterationNodes) {
                    console.warn(
                        `Failed to find project data for project ${teamProject}, aborting update of work item type for work item ${this.getUniqueId()}`
                    );
                    return;
                }

                projectDataWithNodes = {
                    ...projectData,
                    areaNodes,
                    iterationNodes
                };
            }

            const types = await dataManager.beginGetWorkItemTypeData(projectDataWithNodes.guid, [workItemTypeName]);

            if (types && types[0]) {
                this.changeWorkItemType(types[0], projectDataWithNodes);
                this._typeData = types[0];
            }
        }
    }

    private _resetWorkItemTypeChanges() {
        if (this.hasWorkItemTypeChanged()) {
            // Telemetry Info
            const typeInfo: { [key: string]: any } = {
                SourceType: this._originalTypeData.name,
                DestinationType: this.workItemType.name
            };
            if (equals(this.project.guid, this._originalTypeData.projectGuid)) {
                publishEvent(
                    this._pageContext,
                    WITCustomerIntelligenceArea.WORK_ITEM_TRACKING,
                    WITCustomerIntelligenceFeature.CLIENTSIDEOPERATION_WIT_MOVE_REVERT,
                    typeInfo
                );
            } else {
                publishEvent(
                    this._pageContext,
                    WITCustomerIntelligenceArea.WORK_ITEM_TRACKING,
                    WITCustomerIntelligenceFeature.CLIENTSIDEOPERATION_WIT_TYPE_CHANGE_REVERT,
                    typeInfo
                );
            }

            this.changeWorkItemType(this._originalTypeData, this._originalProjectData, this._originalExtensions);
        }
    }

    /**
     * Sets the extensions object and populate the field map
     * @param extensions
     */
    public setExtensions(extensions: IWorkItemTypeExtension[]): void {
        this.extensions = extensions || [];
        this._originalExtensions = this.extensions;
        this._defineExtensionFields();
        this._ruleEngine = undefined; // reset rule engine so that it should pick extension rules
    }

    private _defineExtensionFields() {
        if (this.extensions) {
            this.extensions.forEach(extension => {
                if (extension.markerField) {
                    this._defineField(extension.markerField.field);
                }

                if (extension.fields) {
                    extension.fields.forEach(efield => {
                        this._defineField(efield.field);
                    });
                }
            });
        }
    }

    public addPromiseToWait(promise: Promise<any>) {
        const currentId = this.promiseToWaitId++;
        this.promisesToWait.set(currentId, promise);

        promise.then(undefined, e => {
            this.setError(e);
            this.promisesToWait.delete(currentId);
        });
    }

    private cleanPromisesToWait(): void {
        this.promisesToWait.clear();
        this.promiseToWaitId = 0;
    }

    public async waitForPromisesResolving(): Promise<void> {
        try {
            this.saving.value = true;
            await Promise.all(Array.from(this.promisesToWait.values()));
        } finally {
            this.saving.value = false;
            this.cleanPromisesToWait();
        }
    }

    /**
     * Remove with WebAccess.WorkItemTracking.DateParsingFixDisabled
     */
    public getPageContext(): IVssPageContext {
        return this._pageContext;
    }
}
